import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { 
  Globe, Ship, Anchor, Truck, Package, FileText, DollarSign,
  Plus, Search, Filter, MoreVertical, ArrowRight, Calendar,
  MapPin, Clock, CheckCircle, AlertCircle, Upload, Download,
  Calculator, Warehouse, ChevronRight, RefreshCw, Trash2, Edit,
  Eye, X, Save, ChevronDown, ChevronUp, Building2, User, Mail, Phone,
  Sun, Moon, LogOut, Menu, Settings, Home, TrendingUp, Box,
  ClipboardList, Printer, ArrowUpRight, ArrowDownRight, Scale,
  Hash, Layers, FileCheck, Send, Camera, Users, CreditCard, Receipt, Shield, Copy
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// SettingsTab Component - Matches mechanic settings
function SettingsTab({ businessSlug, businessInfo, user, fetchAllData }) {
  const { isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Super-admin: list of users for this business
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminUsersLoading, setAdminUsersLoading] = useState(false);
  
  // Business Info State
  const [businessInfoForm, setBusinessInfoForm] = useState({
    name: "",
    address: "",
    phone: "",
    email: ""
  });
  
  // User Info State  
  const [userForm, setUserForm] = useState({
    name: "",
    email: "",
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  
  // Tax Settings State
  const [taxSettings, setTaxSettings] = useState({
    default_tax_rate: 0,
    card_fee_enabled: false,
    card_fee_percent: 3,
    tax_exempt_default: false
  });
  
  // Fetch settings on mount
  useEffect(() => {
    fetchSettings();
  }, []);

  // Super-admin-only: fetch users for this business
  useEffect(() => {
    if (!isSuperAdmin || !businessSlug) return;
    let cancelled = false;
    (async () => {
      setAdminUsersLoading(true);
      try {
        const res = await axios.get(`${API}/admin/businesses/by-slug/${businessSlug}/users`);
        if (!cancelled) setAdminUsers(res.data?.users || []);
      } catch (err) {
        if (!cancelled) console.error("Failed to load business users:", err);
      } finally {
        if (!cancelled) setAdminUsersLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [businessSlug, isSuperAdmin]);

  const formatAdminDate = (val) => {
    if (!val) return "—";
    try { return new Date(val).toLocaleString(); } catch { return String(val); }
  };

  const copyAdminText = (text, label) => {
    if (!text) return;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied`)).catch(() => toast.error("Could not copy"));
    }
  };

  const ROLE_BADGE = {
    super_admin: { label: "Super Admin", color: "text-fuchsia-300", bg: "bg-fuchsia-500/15" },
    admin: { label: "Admin", color: "text-purple-300", bg: "bg-purple-500/15" },
    owner: { label: "Owner", color: "text-amber-300", bg: "bg-amber-500/15" },
    manager: { label: "Manager", color: "text-cyan-300", bg: "bg-cyan-500/15" },
    employee: { label: "Employee", color: "text-emerald-300", bg: "bg-emerald-500/15" },
  };
  
  const fetchSettings = async () => {
    setLoading(true);
    try {
      const [settingsRes, bizRes] = await Promise.all([
        axios.get(`${API}/b/${businessSlug}/settings`).catch(() => ({ data: {} })),
        axios.get(`${API}/b/${businessSlug}/info`)
      ]);
      
      const settings = settingsRes.data;
      const biz = bizRes.data;
      
      setBusinessInfoForm({
        name: biz.name || "",
        address: biz.address || "",
        phone: biz.phone || "",
        email: biz.email || ""
      });
      
      setUserForm(prev => ({
        ...prev,
        name: user?.name || "",
        email: user?.email || ""
      }));
      
      setTaxSettings({
        default_tax_rate: settings.tax_settings?.default_tax_rate || 0,
        card_fee_enabled: settings.card_fee_enabled || false,
        card_fee_percent: settings.card_fee_percent || 3,
        tax_exempt_default: settings.tax_exempt_default || false
      });
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSaveBusinessInfo = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/b/${businessSlug}/settings`, {
        business_info: businessInfoForm
      });
      toast.success("Business info saved!");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to save business info");
    } finally {
      setSaving(false);
    }
  };
  
  const handleSaveTaxSettings = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/b/${businessSlug}/settings`, {
        tax_settings: { default_tax_rate: taxSettings.default_tax_rate },
        card_fee_enabled: taxSettings.card_fee_enabled,
        card_fee_percent: taxSettings.card_fee_percent,
        tax_exempt_default: taxSettings.tax_exempt_default
      });
      toast.success("Tax settings saved!");
    } catch (error) {
      toast.error("Failed to save tax settings");
    } finally {
      setSaving(false);
    }
  };
  
  const handleChangePassword = async () => {
    if (userForm.new_password !== userForm.confirm_password) {
      toast.error("Passwords don't match");
      return;
    }
    if (userForm.new_password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: userForm.current_password,
        new_password: userForm.new_password
      });
      toast.success("Password changed successfully!");
      setUserForm(prev => ({ ...prev, current_password: "", new_password: "", confirm_password: "" }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateProfile = async () => {
    if (!userForm.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!userForm.email.trim() || !userForm.email.includes('@')) {
      toast.error("Valid email is required");
      return;
    }
    setSaving(true);
    try {
      const response = await axios.put(`${API}/auth/profile/${user?.id}`, {
        name: userForm.name.trim(),
        email: userForm.email.trim().toLowerCase()
      });
      toast.success("Account info updated successfully!");
      // Update local storage with new user info (must use 'app_user' key to match AuthContext)
      const updatedUser = { ...user, name: response.data.name, email: response.data.email };
      localStorage.setItem('app_user', JSON.stringify(updatedUser));
      // Force page refresh to update sidebar display
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update account info");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-content">
      <div>
        <h2 className="text-3xl font-bold text-white">Settings</h2>
        <p className="text-slate-400 mt-1">Manage your business profile and preferences</p>
      </div>

      {/* Super Admin: Account Owners panel */}
      {isSuperAdmin && (
        <Card className="bg-slate-800/50 border-fuchsia-500/30" data-testid="admin-account-owners-card">
          <CardHeader className="border-b border-slate-700/50">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-fuchsia-400" />
                Account Owners
                <span className="text-xs font-normal text-slate-400">(Super Admin only)</span>
              </CardTitle>
              <span className="text-xs px-2.5 py-1 rounded-full bg-slate-700/50 text-slate-300">
                {adminUsers.length} user{adminUsers.length === 1 ? '' : 's'}
              </span>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            {adminUsersLoading ? (
              <p className="text-sm text-slate-400">Loading users…</p>
            ) : adminUsers.length === 0 ? (
              <p className="text-sm text-slate-400">No users found for this business.</p>
            ) : (
              <div className="space-y-3">
                {adminUsers.map((u) => {
                  const role = ROLE_BADGE[u.role] || { label: u.role || 'User', color: 'text-slate-300', bg: 'bg-slate-500/15' };
                  return (
                    <div
                      key={u.id || u.email}
                      className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-lg bg-slate-700/30 border border-slate-600/50"
                      data-testid={`admin-business-user-${u.email}`}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${role.bg}`}>
                          <User className={`w-5 h-5 ${role.color}`} />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-white">{u.name || '(no name)'}</p>
                            <span className={`text-xs px-2 py-0.5 rounded ${role.bg} ${role.color}`}>{role.label}</span>
                            {u.email_verified === false && (
                              <span className="text-xs px-2 py-0.5 rounded bg-amber-500/15 text-amber-300">Email unverified</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            <span className="text-sm font-mono text-slate-200 break-all">{u.email}</span>
                          </div>
                          <div className="text-xs mt-1 text-slate-500">
                            Created: {formatAdminDate(u.created_at)}
                            {u.last_login && <> &middot; Last login: {formatAdminDate(u.last_login)}</>}
                            {u.id && <> &middot; ID: <span className="font-mono">{u.id.slice(0, 8)}…</span></>}
                          </div>
                        </div>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => copyAdminText(u.email, "Email")}
                        className="border-slate-600 text-slate-200 hover:bg-slate-700"
                        data-testid={`copy-user-email-${u.email}`}
                      >
                        <Copy className="w-3.5 h-3.5 mr-1" /> Copy email
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
            <p className="text-xs mt-4 text-slate-500">
              To create or modify users, go to <span className="font-medium text-slate-400">Admin Panel → Users</span>.
              Passwords are never displayed for security.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Business Information */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Building2 className="w-5 h-5 text-cyan-400" /> Business Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300">Business Name</Label>
              <Input
                value={businessInfoForm.name}
                onChange={(e) => setBusinessInfoForm({ ...businessInfoForm, name: e.target.value })}
                className="bg-slate-700/50 border-slate-600 text-white mt-1"
                placeholder="ILKSOY GROUP, LLC"
              />
            </div>
            <div>
              <Label className="text-slate-300">Phone</Label>
              <Input
                value={businessInfoForm.phone}
                onChange={(e) => setBusinessInfoForm({ ...businessInfoForm, phone: e.target.value })}
                className="bg-slate-700/50 border-slate-600 text-white mt-1"
                placeholder="(555) 123-4567"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-slate-300">Address</Label>
              <Input
                value={businessInfoForm.address}
                onChange={(e) => setBusinessInfoForm({ ...businessInfoForm, address: e.target.value })}
                className="bg-slate-700/50 border-slate-600 text-white mt-1"
                placeholder="123 Warehouse Blvd, City, State ZIP"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-slate-300">Email</Label>
              <Input
                type="email"
                value={businessInfoForm.email}
                onChange={(e) => setBusinessInfoForm({ ...businessInfoForm, email: e.target.value })}
                className="bg-slate-700/50 border-slate-600 text-white mt-1"
                placeholder="contact@ilksoy.com"
              />
            </div>
          </div>
          <Button onClick={handleSaveBusinessInfo} disabled={saving} className="bg-gradient-to-r from-cyan-500 to-blue-600">
            <Save className="w-4 h-4 mr-2" /> Save Business Info
          </Button>
        </CardContent>
      </Card>

      {/* Tax & Fee Settings */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-cyan-400" /> Tax & Fee Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label className="text-slate-300">Default Tax Rate (%)</Label>
              <Input
                type="number"
                step="0.01"
                value={taxSettings.default_tax_rate}
                onChange={(e) => setTaxSettings({ ...taxSettings, default_tax_rate: parseFloat(e.target.value) || 0 })}
                className="bg-slate-700/50 border-slate-600 text-white mt-1"
                placeholder="6.00"
              />
            </div>
            <div>
              <Label className="text-slate-300">Card Fee (%)</Label>
              <Input
                type="number"
                step="0.01"
                value={taxSettings.card_fee_percent}
                onChange={(e) => setTaxSettings({ ...taxSettings, card_fee_percent: parseFloat(e.target.value) || 3 })}
                className="bg-slate-700/50 border-slate-600 text-white mt-1"
                placeholder="3.00"
                disabled={!taxSettings.card_fee_enabled}
              />
            </div>
          </div>
          
          <div className="space-y-4 pt-2">
            <div className="flex items-center justify-between p-4 rounded-lg bg-slate-700/30 border border-slate-600/50">
              <div>
                <p className="font-medium text-white">Enable Card Fee</p>
                <p className="text-sm text-slate-400">Add a processing fee when customers pay by card</p>
              </div>
              <Switch
                checked={taxSettings.card_fee_enabled}
                onCheckedChange={(checked) => setTaxSettings({ ...taxSettings, card_fee_enabled: checked })}
              />
            </div>
            
            <div className="flex items-center justify-between p-4 rounded-lg bg-slate-700/30 border border-slate-600/50">
              <div>
                <p className="font-medium text-white">Tax Exempt by Default</p>
                <p className="text-sm text-slate-400">New invoices will default to tax exempt</p>
              </div>
              <Switch
                checked={taxSettings.tax_exempt_default}
                onCheckedChange={(checked) => setTaxSettings({ ...taxSettings, tax_exempt_default: checked })}
              />
            </div>
          </div>
          
          <Button onClick={handleSaveTaxSettings} disabled={saving} className="bg-gradient-to-r from-cyan-500 to-blue-600">
            <Save className="w-4 h-4 mr-2" /> Save Tax Settings
          </Button>
        </CardContent>
      </Card>

      {/* Account Security */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <User className="w-5 h-5 text-cyan-400" /> Account Security
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300">Account Name</Label>
              <Input
                value={userForm.name}
                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                className="bg-slate-700/50 border-slate-600 text-white mt-1"
                placeholder="Enter your name"
              />
            </div>
            <div>
              <Label className="text-slate-300">Account Email</Label>
              <Input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                className="bg-slate-700/50 border-slate-600 text-white mt-1"
                placeholder="Enter your email"
              />
            </div>
          </div>
          <Button 
            onClick={handleUpdateProfile} 
            disabled={saving}
            className="mt-2 bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/30"
          >
            <Save className="w-4 h-4 mr-2" /> Save Account Info
          </Button>
          
          <div className="pt-4 border-t border-slate-700/50">
            <p className="font-medium text-white mb-4">Change Password</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-slate-300">Current Password</Label>
                <Input
                  type="password"
                  value={userForm.current_password}
                  onChange={(e) => setUserForm({ ...userForm, current_password: e.target.value })}
                  className="bg-slate-700/50 border-slate-600 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-300">New Password</Label>
                <Input
                  type="password"
                  value={userForm.new_password}
                  onChange={(e) => setUserForm({ ...userForm, new_password: e.target.value })}
                  className="bg-slate-700/50 border-slate-600 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-300">Confirm Password</Label>
                <Input
                  type="password"
                  value={userForm.confirm_password}
                  onChange={(e) => setUserForm({ ...userForm, confirm_password: e.target.value })}
                  className="bg-slate-700/50 border-slate-600 text-white mt-1"
                />
              </div>
            </div>
            <Button 
              onClick={handleChangePassword} 
              disabled={saving || !userForm.current_password || !userForm.new_password}
              className="mt-4 bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30"
            >
              Change Password
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Status configurations
const STATUS_CONFIG = {
  on_water: { bg: "bg-blue-500/20", text: "text-blue-400", label: "On Water", icon: Ship },
  at_port: { bg: "bg-amber-500/20", text: "text-amber-400", label: "At Port", icon: Anchor },
  customs_hold: { bg: "bg-orange-500/20", text: "text-orange-400", label: "Customs", icon: AlertCircle },
  in_transit: { bg: "bg-purple-500/20", text: "text-purple-400", label: "In Transit", icon: Truck },
  received: { bg: "bg-emerald-500/20", text: "text-emerald-400", label: "Received", icon: CheckCircle },
  pending: { bg: "bg-amber-500/20", text: "text-amber-400", label: "Pending" },
  partial: { bg: "bg-blue-500/20", text: "text-blue-400", label: "Partial" },
  paid: { bg: "bg-emerald-500/20", text: "text-emerald-400", label: "Paid" }
};

export default function WholesaleDashboard() {
  const { businessSlug } = useParams();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { logout, user, isSuperAdmin } = useAuth();
  const isDark = theme === "dark";
  
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Data states
  const [dashboard, setDashboard] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [products, setProducts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [businessInfo, setBusinessInfo] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paymentsData, setPaymentsData] = useState({ total_amount: 0, category_totals: {}, count: 0 });
  
  // Modal states
  const [showShipmentModal, setShowShipmentModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showInvoiceDetailModal, setShowInvoiceDetailModal] = useState(false);
  const [showLandedCostModal, setShowLandedCostModal] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showDeleteProductConfirm, setShowDeleteProductConfirm] = useState(null);
  const [editingShipment, setEditingShipment] = useState(null);
  const [showShipmentDetailModal, setShowShipmentDetailModal] = useState(false);
  const [showDeleteShipmentConfirm, setShowDeleteShipmentConfirm] = useState(null);
  
  // Bulk selection state for inventory
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  
  // Customer modal states
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [showDeleteCustomerConfirm, setShowDeleteCustomerConfirm] = useState(null);
  
  // Payment modal states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [showDeletePaymentConfirm, setShowDeletePaymentConfirm] = useState(null);
  const [selectedPaymentCategory, setSelectedPaymentCategory] = useState("all");
  
  // Image preview state
  const [previewImage, setPreviewImage] = useState(null);
  
  // Upload state for progress tracking
  const [uploadingState, setUploadingState] = useState({ active: false, current: 0, total: 0, filename: '' });
  const [customerUploadingState, setCustomerUploadingState] = useState({ active: false, current: 0, total: 0, filename: '' });
  
  // Country filter for inventory
  const [selectedCountry, setSelectedCountry] = useState("all");
  
  // Customer filter for invoices
  const [selectedCustomer, setSelectedCustomer] = useState("all");
  
  // Form states
  const [shipmentForm, setShipmentForm] = useState({
    shipment_date: new Date().toISOString().split('T')[0], // Default to today, editable
    container_number: "", bill_of_lading: "", customs_entry_number: "",
    origin_country: "", origin_port: "", destination_port: "", final_destination: "",
    estimated_arrival: "", total_units: "", total_cartons: "", notes: "", status: "booked"
  });
  
  const [productForm, setProductForm] = useState({
    sku: "", name: "", description: "", category: "", hs_code: "",
    purchase_price: "", sell_price: "", stock_quantity: "",
    units_per_carton: "", carton_type: "carton", warehouse_location: "",
    country_of_origin: "", supplier: "", image_url: ""
  });
  
  // Invoice form - Full wholesale invoice
  const [invoiceForm, setInvoiceForm] = useState({
    invoice_date: new Date().toISOString().split('T')[0], // Default to today, editable
    consignee_name: "",
    consignee_company: "",
    consignee_email: "",
    consignee_phone: "",
    consignee_address: "",
    bill_of_lading_ref: "",
    ship_from_warehouse: "",
    final_destination: "",
    payment_terms: "due_on_receipt",
    show_50_percent_terms: false, // Show "50% due at purchase, 50% after delivery"
    notes: "",
    custom_message: "Thank you for your business!",
    // Tax & fee options
    tax_rate: 0,
    is_tax_exempt: false,
    card_fee_enabled: false,
    card_fee_percent: 3,
    // Line items
    items: [{ description: "", sku: "", product_id: "", image_url: "", cases: 1, qty_per_case: 1, quantity: 1, unit: "units", pkg_type: "boxes", weight_lbs: 0, pallets: 0, unit_price: 0 }]
  });
  
  const [landedCostForm, setLandedCostForm] = useState({
    fob_price: 0, freight: 0, duty_tariff: 0, drayage: 0,
    insurance: 0, other_fees: 0, total_units: 1, sell_price: 0
  });
  
  const [landedCostResult, setLandedCostResult] = useState(null);
  
  // Customer form
  const [customerForm, setCustomerForm] = useState({
    name: "", company: "", email: "", phone: "",
    address: "", city: "", state: "", zip_code: "",
    country: "", notes: "", credit_terms: ""
  });
  
  // Payment form
  const [paymentForm, setPaymentForm] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: "",
    category: "order_payment",
    description: "",
    reference: "",
    linked_shipment_id: "",
    linked_invoice_id: "",
    payment_method: "",
    recipient: "",
    notes: ""
  });
  
  // Payment categories
  const PAYMENT_CATEGORIES = [
    { value: "order_payment", label: "Order Payment", color: "text-blue-500" },
    { value: "border_fee", label: "Border/Customs Fee", color: "text-amber-500" },
    { value: "shipping", label: "Shipping Cost", color: "text-purple-500" },
    { value: "customs", label: "Customs Duty", color: "text-orange-500" },
    { value: "drayage", label: "Drayage/Trucking", color: "text-green-500" },
    { value: "insurance", label: "Insurance", color: "text-cyan-500" },
    { value: "other", label: "Other Expense", color: "text-slate-500" }
  ];
  
  const [lastUpdated, setLastUpdated] = useState(null);
  
  // Fetch data on mount - only if businessSlug is valid
  useEffect(() => {
    if (businessSlug && businessSlug !== 'undefined') {
      fetchAllData();
    }
  }, [businessSlug]);
  
  // Refetch data when tab becomes visible again (in case it was sleeping)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && businessSlug) {
        // Only refetch if data looks stale (more than 5 minutes old)
        if (lastUpdated && (Date.now() - lastUpdated.getTime() > 5 * 60 * 1000)) {
          // Tab visible — refresh stale data
          fetchAllData();
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [businessSlug, lastUpdated]);
  
  const [dataError, setDataError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const fetchAllData = async (isRetry = false) => {
    if (!businessSlug || businessSlug === 'undefined') {
      console.warn("No valid businessSlug, skipping fetch");
      return;
    }
    
    setLoading(true);
    setDataError(null);
    
    try {
      // Fetch each endpoint with individual error handling
      const fetchWithFallback = async (url, fallback) => {
        try {
          const res = await axios.get(url, { timeout: 15000 }); // 15 second timeout
          return res.data;
        } catch (err) {
          console.warn(`Failed to fetch ${url}:`, err.message);
          return fallback;
        }
      };

      const [dashData, shipData, prodData, invData, whData, bizData, custData, payData] = await Promise.all([
        fetchWithFallback(`${API}/b/${businessSlug}/wholesale/dashboard`, { 
          inventory_value: 0, total_revenue: 0, pending_invoices: 0, 
          in_transit_value: 0, total_profit: 0, shipment_counts: {} 
        }),
        fetchWithFallback(`${API}/b/${businessSlug}/wholesale/shipments`, []),
        fetchWithFallback(`${API}/b/${businessSlug}/wholesale/products`, []),
        fetchWithFallback(`${API}/b/${businessSlug}/wholesale/invoices`, []),
        fetchWithFallback(`${API}/b/${businessSlug}/wholesale/warehouses`, []),
        fetchWithFallback(`${API}/b/${businessSlug}/info`, {}),
        fetchWithFallback(`${API}/b/${businessSlug}/wholesale/customers`, []),
        fetchWithFallback(`${API}/b/${businessSlug}/wholesale/payments`, { payments: [], total_amount: 0, category_totals: {}, count: 0 })
      ]);

      setDashboard(dashData);
      setShipments(shipData);
      setProducts(prodData);
      setInvoices(invData);
      setWarehouses(whData);
      setBusinessInfo(bizData);
      setCustomers(custData);
      setPayments(payData.payments || []);
      setPaymentsData({
        total_amount: payData.total_amount || 0,
        category_totals: payData.category_totals || {},
        count: payData.count || 0
      });

      // Check if we got empty data when we shouldn't have
      const hasNoData = !shipData.length && !prodData.length && !invData.length;
      if (hasNoData && !isRetry && retryCount < 2) {
        // No data yet — retry
        setRetryCount(prev => prev + 1);
        setTimeout(() => fetchAllData(true), 2000); // Retry after 2 seconds
        return;
      }
      
      setRetryCount(0); // Reset retry count on success
      setLastUpdated(new Date()); // Track when data was loaded
      
    } catch (error) {
      console.error("Error fetching data:", error);
      setDataError("Failed to load data. Click Refresh to try again.");
      toast.error("Connection issue - some data may not have loaded. Click Refresh.");
    } finally {
      setLoading(false);
    }
  };
  
  // Shipment handlers
  const handleCreateShipment = async () => {
    try {
      await axios.post(`${API}/b/${businessSlug}/wholesale/shipments`, shipmentForm);
      toast.success("Shipment created");
      setShowShipmentModal(false);
      resetShipmentForm();
      fetchAllData();
    } catch (error) {
      toast.error("Failed to create shipment");
    }
  };
  
  const resetShipmentForm = () => {
    setShipmentForm({
      shipment_date: new Date().toISOString().split('T')[0],
      container_number: "", bill_of_lading: "", customs_entry_number: "",
      origin_country: "", origin_port: "", destination_port: "", final_destination: "",
      estimated_arrival: "", total_units: "", total_cartons: "", notes: "", status: "booked"
    });
  };

  const handleEditShipment = (shipment) => {
    setEditingShipment(shipment);
    setShipmentForm({
      shipment_date: shipment.shipment_date || shipment.created_at?.split('T')[0] || new Date().toISOString().split('T')[0],
      container_number: shipment.container_number || "",
      bill_of_lading: shipment.bill_of_lading || "",
      customs_entry_number: shipment.customs_entry_number || "",
      origin_country: shipment.origin_country || "",
      origin_port: shipment.origin_port || "",
      destination_port: shipment.destination_port || "",
      final_destination: shipment.final_destination || "",
      estimated_arrival: shipment.estimated_arrival || "",
      total_units: shipment.total_units ?? "",
      total_cartons: shipment.total_cartons ?? "",
      notes: shipment.notes || "",
      status: shipment.status || "booked"
    });
    setShowShipmentDetailModal(true);
  };

  const handleUpdateShipment = async () => {
    try {
      await axios.put(`${API}/b/${businessSlug}/wholesale/shipments/${editingShipment.id}`, shipmentForm);
      toast.success("Shipment updated");
      setShowShipmentDetailModal(false);
      setEditingShipment(null);
      resetShipmentForm();
      fetchAllData();
    } catch (error) {
      toast.error("Failed to update shipment");
    }
  };
  
  const handleUpdateShipmentStatus = async (shipmentId, status) => {
    try {
      await axios.post(`${API}/b/${businessSlug}/wholesale/shipments/${shipmentId}/milestone?status=${status}`);
      toast.success(`Status updated to ${STATUS_CONFIG[status]?.label}`);
      fetchAllData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleDeleteShipment = async (shipmentId) => {
    try {
      await axios.delete(`${API}/b/${businessSlug}/wholesale/shipments/${shipmentId}`);
      toast.success("Shipment deleted");
      setShowDeleteShipmentConfirm(null);
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete shipment");
    }
  };
  
  // Product handlers
  const handleCreateProduct = async () => {
    if (!productForm.sku || !productForm.name) {
      toast.error("SKU and Product Name are required");
      return;
    }
    try {
      // Convert empty strings to proper values for numeric fields
      const payload = {
        ...productForm,
        purchase_price: productForm.purchase_price ? parseFloat(productForm.purchase_price) : 0,
        sell_price: productForm.sell_price ? parseFloat(productForm.sell_price) : 0,
        stock_quantity: productForm.stock_quantity ? parseInt(productForm.stock_quantity) : 0,
        units_per_carton: productForm.units_per_carton ? parseInt(productForm.units_per_carton) : 1
      };
      await axios.post(`${API}/b/${businessSlug}/wholesale/products`, payload);
      toast.success("Product created");
      setShowProductModal(false);
      resetProductForm();
      fetchAllData();
    } catch (error) {
      const errMsg = error.response?.data?.detail;
      toast.error(typeof errMsg === 'string' ? errMsg : "Failed to create product");
    }
  };
  
  const resetProductForm = () => {
    setProductForm({
      sku: "", name: "", description: "", category: "", hs_code: "",
      purchase_price: "", sell_price: "", stock_quantity: "",
      units_per_carton: "", carton_type: "carton", warehouse_location: "",
      country_of_origin: "", supplier: "", image_url: ""
    });
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      sku: product.sku || "",
      name: product.name || "",
      description: product.description || "",
      category: product.category || "",
      hs_code: product.hs_code || "",
      purchase_price: product.purchase_price ?? product.fob_price ?? "",
      sell_price: product.sell_price ?? "",
      stock_quantity: product.stock_quantity ?? "",
      units_per_carton: product.units_per_carton ?? "",
      carton_type: product.carton_type || "carton",
      warehouse_location: product.warehouse_location || "",
      country_of_origin: product.country_of_origin || "",
      supplier: product.supplier || "",
      image_url: product.image_url || ""
    });
    setShowProductModal(true);
  };

  const handleUpdateProduct = async () => {
    try {
      await axios.put(`${API}/b/${businessSlug}/wholesale/products/${editingProduct.id}`, productForm);
      toast.success("Product updated successfully");
      setShowProductModal(false);
      setEditingProduct(null);
      resetProductForm();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update product");
    }
  };

  const handleDeleteProduct = async (productId) => {
    try {
      await axios.delete(`${API}/b/${businessSlug}/wholesale/products/${productId}`);
      toast.success("Product deleted");
      setShowDeleteProductConfirm(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete product");
    }
  };

  // Bulk delete handler
  const handleBulkDeleteProducts = async () => {
    if (selectedProductIds.length === 0) {
      toast.error("No products selected");
      return;
    }
    try {
      const response = await axios.post(`${API}/b/${businessSlug}/wholesale/products/bulk-delete`, selectedProductIds);
      toast.success(`${response.data.deleted_count} products deleted`);
      setSelectedProductIds([]);
      setShowBulkDeleteConfirm(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete products");
    }
  };

  // Delete all products
  const handleDeleteAllProducts = async () => {
    try {
      const response = await axios.delete(`${API}/b/${businessSlug}/wholesale/products/all`);
      toast.success(`${response.data.deleted_count} products deleted`);
      setSelectedProductIds([]);
      setShowBulkDeleteConfirm(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete products");
    }
  };

  // Toggle product selection
  const toggleProductSelection = (productId) => {
    setSelectedProductIds(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  // Select/deselect all products
  const toggleSelectAll = () => {
    if (selectedProductIds.length === products.length) {
      setSelectedProductIds([]);
    } else {
      setSelectedProductIds(products.map(p => p.id));
    }
  };
  
  // Invoice handlers - supports both create and edit
  const handleCreateInvoice = async () => {
    try {
      // Process items for API - include all packaging details for customer invoices
      const processedItems = invoiceForm.items.map(item => ({
        product_id: "",
        sku: item.sku,
        name: item.description,
        quantity_units: item.quantity,
        quantity_cartons: 0,
        unit_price: item.unit_price,
        weight_lbs: item.weight_lbs || 0,
        pallets: item.pallets || 0,
        unit: item.unit || 'units',
        pkg_type: item.pkg_type || 'boxes',
        qty_per_case: item.qty_per_case || 1,
        cases: item.cases || item.quantity
      }));
      
      const payload = {
        ...invoiceForm,
        items: processedItems
      };
      
      let res;
      if (editingInvoiceId) {
        // Update existing invoice
        res = await axios.put(`${API}/b/${businessSlug}/wholesale/invoices/${editingInvoiceId}`, payload);
        toast.success(`Invoice updated!`);
        // Fetch the updated invoice details
        const updatedInvoice = await axios.get(`${API}/b/${businessSlug}/wholesale/invoices/${editingInvoiceId}`);
        setSelectedInvoice(updatedInvoice.data);
      } else {
        // Create new invoice
        res = await axios.post(`${API}/b/${businessSlug}/wholesale/invoices`, payload);
        toast.success(`Invoice ${res.data.invoice_number} created!`);
        // Fetch the newly created invoice details
        const newInvoice = await axios.get(`${API}/b/${businessSlug}/wholesale/invoices/${res.data.id}`);
        setSelectedInvoice(newInvoice.data);
      }
      
      setShowInvoiceModal(false);
      resetInvoiceForm();
      setEditingInvoiceId(null);
      // Navigate to invoice detail page
      setShowInvoiceDetailModal(true);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save invoice");
    }
  };
  
  const resetInvoiceForm = () => {
    setInvoiceForm({
      invoice_date: new Date().toISOString().split('T')[0],
      consignee_name: "", consignee_company: "", consignee_email: "",
      consignee_phone: "", consignee_address: "", bill_of_lading_ref: "",
      ship_from_warehouse: "", final_destination: "", payment_terms: "due_on_receipt",
      show_50_percent_terms: false,
      notes: "", custom_message: "Thank you for your business!",
      tax_rate: 0, is_tax_exempt: false, card_fee_enabled: false, card_fee_percent: 3,
      items: [{ description: "", sku: "", product_id: "", image_url: "", cases: 1, qty_per_case: 1, quantity: 1, unit: "units", pkg_type: "boxes", weight_lbs: 0, pallets: 0, unit_price: 0 }]
    });
  };
  
  const addInvoiceItem = () => {
    setInvoiceForm({
      ...invoiceForm,
      items: [...invoiceForm.items, { description: "", sku: "", product_id: "", image_url: "", cases: 1, qty_per_case: 1, quantity: 1, unit: "units", pkg_type: "boxes", weight_lbs: 0, pallets: 0, unit_price: 0 }]
    });
  };
  
  const removeInvoiceItem = (index) => {
    if (invoiceForm.items.length > 1) {
      setInvoiceForm({
        ...invoiceForm,
        items: invoiceForm.items.filter((_, i) => i !== index)
      });
    }
  };

  const moveInvoiceItemUp = (index) => {
    if (index === 0) return;
    const newItems = [...invoiceForm.items];
    [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
    setInvoiceForm({ ...invoiceForm, items: newItems });
  };

  const moveInvoiceItemDown = (index) => {
    if (index === invoiceForm.items.length - 1) return;
    const newItems = [...invoiceForm.items];
    [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
    setInvoiceForm({ ...invoiceForm, items: newItems });
  };
  
  const updateInvoiceItem = (index, field, value) => {
    const newItems = [...invoiceForm.items];
    newItems[index][field] = value;
    
    // Auto-calculate Total Units when Cases or Qty/Case changes (unless manually editing quantity)
    if (field === 'cases' || field === 'qty_per_case') {
      const cases = field === 'cases' ? (value || 1) : (newItems[index].cases || 1);
      const qtyPerCase = field === 'qty_per_case' ? (value || 1) : (newItems[index].qty_per_case || 1);
      newItems[index].quantity = cases * qtyPerCase;
      newItems[index].manualQuantity = false; // Reset manual flag when auto-calculating
    }
    
    // If user manually edits quantity, set a flag so we don't auto-override
    if (field === 'quantity') {
      newItems[index].manualQuantity = true;
    }
    
    setInvoiceForm({ ...invoiceForm, items: newItems });
  };
  
  // Landed cost calculator
  const calculateLandedCost = async () => {
    try {
      const params = new URLSearchParams(landedCostForm);
      const res = await axios.post(`${API}/b/${businessSlug}/wholesale/calculate-landed-cost?${params}`);
      setLandedCostResult(res.data);
    } catch (error) {
      toast.error("Failed to calculate");
    }
  };
  
  const handleLogout = () => {
    logout();
    window.location.href = "/login";
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };
  
  // Calculate invoice totals
  const calculateInvoiceTotals = () => {
    const subtotal = invoiceForm.items.reduce((sum, item) => {
      // Use quantity directly (it's either manually entered or calculated from cases × qty_per_case)
      const totalQty = item.quantity || ((item.cases || 1) * (item.qty_per_case || 1));
      return sum + (totalQty * (item.unit_price || 0));
    }, 0);
    const totalWeight = invoiceForm.items.reduce((sum, item) => sum + (item.weight_lbs || 0), 0);
    const totalPallets = invoiceForm.items.reduce((sum, item) => sum + (item.pallets || 0), 0);
    const totalCases = invoiceForm.items.reduce((sum, item) => sum + (item.cases || 1), 0);
    const totalQty = invoiceForm.items.reduce((sum, item) => sum + (item.quantity || ((item.cases || 1) * (item.qty_per_case || 1))), 0);
    
    // Tax calculation
    const taxAmount = invoiceForm.is_tax_exempt ? 0 : subtotal * (invoiceForm.tax_rate / 100);
    
    // Card fee calculation
    const cardFeeAmount = invoiceForm.card_fee_enabled ? (subtotal + taxAmount) * (invoiceForm.card_fee_percent / 100) : 0;
    
    const total = subtotal + taxAmount + cardFeeAmount;
    
    return { subtotal, totalWeight, totalPallets, totalCases, totalQty, taxAmount, cardFeeAmount, total };
  };

  // ==================== TEMPLATE DOWNLOAD ====================
  const downloadTemplate = (kind) => {
    const TEMPLATES = {
      products: {
        filename: 'inventory_template.csv',
        csv:
          'sku,name,description,category,purchase_price,sell_price,stock_quantity,units_per_carton,country_of_origin,supplier\n' +
          'PRD-001,Sample Product,Brief description,Snacks,5.50,9.99,100,12,Turkey,Acme Trading\n' +
          'PRD-002,Another Item,Optional desc,Beverages,2.25,4.50,200,24,Turkey,\n',
      },
      customers: {
        filename: 'customers_template.csv',
        csv:
          'name,company,email,phone,address,city,state,zip_code,country,notes,credit_terms\n' +
          'John Doe,Doe Distribution,john@example.com,555-123-4567,123 Main St,Baltimore,MD,21201,USA,Reliable buyer,Net 30\n' +
          'Jane Smith,Smith Imports,jane@example.com,555-987-6543,,,,USA,,COD\n',
      },
      invoices: {
        filename: 'invoice_template.csv',
        csv:
          'invoice_number,invoice_date,consignee_company,consignee_name,consignee_email,consignee_phone,sku,product_name,quantity_units,unit_price\n' +
          'INV-1001,2026-02-15,ABC Corp,Buyer Name,buyer@example.com,555-111-2222,PRD-001,Sample Product,50,9.99\n',
      },
      shipments: {
        filename: 'shipment_template.csv',
        csv:
          'container_number,bill_of_lading,origin_country,origin_port,destination_port,vessel_name,departure_date,arrival_date,carrier,total_cartons,total_weight_kg,freight_cost,notes\n' +
          'MSCU1234567,BL-2026-001,Turkey,Mersin,Baltimore,MV Star,2026-02-01,2026-02-25,MSC,250,8500,3200,Mixed pallet shipment\n',
      },
    };
    const t = TEMPLATES[kind];
    if (!t) return;
    const blob = new Blob([t.csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = t.filename;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success(`Downloaded ${t.filename}`);
  };

  // ==================== CUSTOMER HANDLERS ====================
  const resetCustomerForm = () => {
    setCustomerForm({
      name: "", company: "", email: "", phone: "",
      address: "", city: "", state: "", zip_code: "",
      country: "", notes: "", credit_terms: ""
    });
  };

  const handleCreateCustomer = async () => {
    if (!customerForm.name.trim()) {
      toast.error("Customer name is required");
      return;
    }
    try {
      await axios.post(`${API}/b/${businessSlug}/wholesale/customers`, customerForm);
      toast.success("Customer created");
      setShowCustomerModal(false);
      resetCustomerForm();
      fetchAllData();
    } catch (error) {
      toast.error("Failed to create customer");
    }
  };

  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setCustomerForm({
      name: customer.name || "",
      company: customer.company || "",
      email: customer.email || "",
      phone: customer.phone || "",
      address: customer.address || "",
      city: customer.city || "",
      state: customer.state || "",
      zip_code: customer.zip_code || "",
      country: customer.country || "",
      notes: customer.notes || "",
      credit_terms: customer.credit_terms || ""
    });
    setShowCustomerModal(true);
  };

  const handleUpdateCustomer = async () => {
    if (!customerForm.name.trim()) {
      toast.error("Customer name is required");
      return;
    }
    try {
      await axios.put(`${API}/b/${businessSlug}/wholesale/customers/${editingCustomer.id}`, customerForm);
      toast.success("Customer updated");
      setShowCustomerModal(false);
      setEditingCustomer(null);
      resetCustomerForm();
      fetchAllData();
    } catch (error) {
      toast.error("Failed to update customer");
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    try {
      await axios.delete(`${API}/b/${businessSlug}/wholesale/customers/${customerId}`);
      toast.success("Customer deleted");
      setShowDeleteCustomerConfirm(null);
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete customer");
    }
  };

  // ==================== PAYMENT HANDLERS ====================
  const resetPaymentForm = () => {
    setPaymentForm({
      date: new Date().toISOString().split('T')[0],
      amount: "",
      category: "order_payment",
      description: "",
      reference: "",
      linked_shipment_id: "",
      linked_invoice_id: "",
      payment_method: "",
      recipient: "",
      notes: ""
    });
  };

  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);

  const handleCreatePayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error("Amount is required");
      return;
    }
    if (isSubmittingPayment) return; // Prevent double-submit
    
    setIsSubmittingPayment(true);
    try {
      await axios.post(`${API}/b/${businessSlug}/wholesale/payments`, {
        ...paymentForm,
        amount: parseFloat(paymentForm.amount)
      });
      toast.success("Payment recorded");
      setShowPaymentModal(false);
      resetPaymentForm();
      fetchAllData();
    } catch (error) {
      console.error("Payment error:", error);
      const errorMsg = error.response?.data?.detail || error.message || "Failed to record payment";
      toast.error(errorMsg);
    } finally {
      setIsSubmittingPayment(false);
    }
  };

  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
    setPaymentForm({
      date: payment.date || new Date().toISOString().split('T')[0],
      amount: payment.amount || "",
      category: payment.category || "order_payment",
      description: payment.description || "",
      reference: payment.reference || "",
      linked_shipment_id: payment.linked_shipment_id || "",
      linked_invoice_id: payment.linked_invoice_id || "",
      payment_method: payment.payment_method || "",
      recipient: payment.recipient || "",
      notes: payment.notes || ""
    });
    setShowPaymentModal(true);
  };

  const handleUpdatePayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error("Amount is required");
      return;
    }
    if (isSubmittingPayment) return; // Prevent double-submit
    
    setIsSubmittingPayment(true);
    try {
      await axios.put(`${API}/b/${businessSlug}/wholesale/payments/${editingPayment.id}`, {
        ...paymentForm,
        amount: parseFloat(paymentForm.amount)
      });
      toast.success("Payment updated");
      setShowPaymentModal(false);
      setEditingPayment(null);
      resetPaymentForm();
      fetchAllData();
    } catch (error) {
      console.error("Payment update error:", error);
      const errorMsg = error.response?.data?.detail || error.message || "Failed to update payment";
      toast.error(errorMsg);
    } finally {
      setIsSubmittingPayment(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    try {
      await axios.delete(`${API}/b/${businessSlug}/wholesale/payments/${paymentId}`);
      toast.success("Payment deleted");
      setShowDeletePaymentConfirm(null);
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete payment");
    }
  };

  const filteredPayments = selectedPaymentCategory === "all" 
    ? payments 
    : payments.filter(p => p.category === selectedPaymentCategory);

  // Navigation items
  const navItems = [
    { id: "dashboard", label: "Dashboard", icon: Home },
    { id: "shipments", label: "Shipments", icon: Ship },
    { id: "inventory", label: "Inventory", icon: Package },
    { id: "invoices", label: "Invoices", icon: FileText },
    { id: "customers", label: "Customers", icon: Users },
    { id: "payments", label: "Payments", icon: CreditCard },
    { id: "calculator", label: "Landed Cost", icon: Calculator },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900' : 'bg-gradient-to-br from-gray-50 via-white to-gray-100'}`}>
        <div className="text-center">
          <RefreshCw className="w-10 h-10 text-cyan-400 animate-spin mx-auto mb-4" />
          <p className={isDark ? 'text-slate-400' : 'text-gray-600'}>Loading ILKSOY Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900' : 'bg-gradient-to-br from-gray-50 via-white to-gray-100'}`}>
      {/* Sidebar - Desktop */}
      <aside className={`hidden lg:flex flex-col w-72 fixed h-screen border-r backdrop-blur-xl ${isDark ? 'border-slate-700/50 bg-slate-900/80' : 'border-gray-200 bg-white/90'}`}>
        {/* Logo/Brand */}
        <div className={`p-4 border-b ${isDark ? 'border-slate-700/50' : 'border-gray-200'}`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center shrink-0">
              {businessInfo?.logo_url ? (
                <img src={businessInfo.logo_url} alt="Logo" className="w-10 h-10 object-contain rounded-lg" />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                  <Globe className="w-5 h-5 text-white" strokeWidth={2} />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{businessInfo?.name || 'ILKSOY GROUP'}</p>
              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Import & Distribution</p>
            </div>
          </div>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-2">
            {navItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => setActiveTab(item.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3.5 text-sm font-medium tracking-wide transition-all duration-200 rounded-xl ${
                    activeTab === item.id
                      ? 'text-white bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 shadow-lg shadow-cyan-500/10'
                      : isDark ? 'text-slate-400 hover:text-white hover:bg-slate-800/50' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                  data-testid={`nav-${item.id}`}
                >
                  <item.icon className={`w-5 h-5 ${activeTab === item.id ? 'text-cyan-400' : ''}`} />
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
          
          {/* Quick Stats in Sidebar */}
          <div className={`mt-8 p-4 rounded-xl border ${isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-gray-50 border-gray-200'}`}>
            <p className={`text-xs font-medium mb-3 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>QUICK OVERVIEW</p>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>In Transit</span>
                <span className="text-amber-500 font-bold">{dashboard?.shipments?.on_water || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>At Port</span>
                <span className="text-blue-500 font-bold">{dashboard?.shipments?.at_port || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Pending $</span>
                <span className="text-cyan-500 font-bold">{formatCurrency(dashboard?.financials?.pending_revenue)}</span>
              </div>
            </div>
          </div>
        </nav>

        {/* User Section */}
        <div className={`p-4 border-t ${isDark ? 'border-slate-700/50' : 'border-gray-200'}`}>
          <div className={`flex items-center gap-3 mb-4 p-3 rounded-xl ${isDark ? 'bg-slate-800/50' : 'bg-gray-100'}`}>
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{user?.name || 'User'}</p>
              <p className={`text-xs truncate ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>{user?.email}</p>
            </div>
          </div>
          {isSuperAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin')}
              className="w-full mb-2 border-purple-500/50 text-purple-400 hover:bg-purple-500/10 hover:border-purple-500"
            >
              <Home className="w-4 h-4 mr-2" />
              Admin Panel
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            className={`w-full text-red-500 hover:bg-red-500/10 hover:border-red-500/50 ${isDark ? 'border-slate-700' : 'border-gray-300'}`}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Log Out
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className={`lg:hidden fixed top-0 left-0 right-0 z-50 ${isDark ? 'bg-slate-900/95' : 'bg-white/95'} backdrop-blur-xl border-b ${isDark ? 'border-slate-700/50' : 'border-gray-200'}`}>
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
              <Globe className="w-5 h-5 text-white" />
            </div>
            <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{businessInfo?.name || 'ILKSOY'}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={toggleTheme} 
              className={isDark ? 'text-white hover:bg-slate-800' : 'text-gray-700 hover:bg-gray-100'}
              title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className={isDark ? 'text-white' : 'text-gray-700'}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>
        {mobileMenuOpen && (
          <nav className={`p-4 ${isDark ? 'bg-slate-800/95 border-slate-700/50' : 'bg-gray-50 border-gray-200'} border-t`}>
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => { setActiveTab(item.id); setMobileMenuOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg mb-1 ${
                  activeTab === item.id
                    ? 'bg-cyan-500/20 text-cyan-400'
                    : isDark ? 'text-slate-400' : 'text-gray-600'
                }`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </button>
            ))}
            {isSuperAdmin && (
              <button 
                onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }} 
                className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-purple-400 rounded-lg mt-2 hover:bg-purple-500/10"
              >
                <Home className="w-5 h-5" />
                Admin Panel
              </button>
            )}
            <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-red-400 rounded-lg mt-2">
              <LogOut className="w-5 h-5" />
              Log Out
            </button>
          </nav>
        )}
      </div>

      {/* Main Content */}
      <main className={`lg:ml-72 p-4 lg:p-8 pt-20 lg:pt-8 min-h-screen ${isDark ? 'bg-[#0a0a0f]' : 'bg-gray-50'}`}>
        
        {/* Desktop Theme Toggle - Top Right */}
        <div className="hidden lg:flex justify-end mb-4">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={toggleTheme}
            className={isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'}
          >
            {isDark ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
            {isDark ? 'Light Mode' : 'Dark Mode'}
          </Button>
        </div>
        
        {/* Dashboard Tab */}
        {activeTab === "dashboard" && (
          <div className="space-y-6" data-testid="dashboard-content">
            {/* Error Banner */}
            {dataError && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4" />
                  <span>{dataError}</span>
                </div>
                <Button onClick={() => fetchAllData()} size="sm" variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/20">
                  Retry
                </Button>
              </div>
            )}
            
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <h2 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Dashboard</h2>
                <p className={isDark ? 'text-slate-400 mt-1' : 'text-gray-500 mt-1'}>
                  Welcome back to {businessInfo?.name || 'ILKSOY'}
                  {lastUpdated && (
                    <span className="text-xs ml-2 opacity-60">
                      • Last updated: {lastUpdated.toLocaleTimeString()}
                    </span>
                  )}
                </p>
              </div>
              <div className="flex gap-3">
                <Button onClick={() => fetchAllData()} variant="outline" className={isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> {loading ? 'Loading...' : 'Refresh'}
                </Button>
                <Button onClick={() => setShowInvoiceModal(true)} className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white shadow-lg shadow-cyan-500/25">
                  <Plus className="w-4 h-4 mr-2" /> New Invoice
                </Button>
              </div>
            </div>
            
            {/* Main Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className={`backdrop-blur-sm overflow-hidden relative ${isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-gray-200 shadow-sm'}`}>
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-500/20 to-transparent rounded-bl-full" />
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>In Transit</p>
                      <p className={`text-3xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{dashboard?.shipments?.on_water || 0}</p>
                      <p className="text-xs text-blue-500 mt-2 flex items-center">
                        <Ship className="w-3 h-3 mr-1" /> Shipments on water
                      </p>
                    </div>
                    <div className="p-3 rounded-xl bg-blue-500/20">
                      <Ship className="w-6 h-6 text-blue-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className={`backdrop-blur-sm overflow-hidden relative ${isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-gray-200 shadow-sm'}`}>
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-amber-500/20 to-transparent rounded-bl-full" />
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>At Port</p>
                      <p className={`text-3xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{dashboard?.shipments?.at_port || 0}</p>
                      <p className="text-xs text-amber-500 mt-2 flex items-center">
                        <Anchor className="w-3 h-3 mr-1" /> Awaiting customs
                      </p>
                    </div>
                    <div className="p-3 rounded-xl bg-amber-500/20">
                      <Anchor className="w-6 h-6 text-amber-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className={`backdrop-blur-sm overflow-hidden relative ${isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-gray-200 shadow-sm'}`}>
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-emerald-500/20 to-transparent rounded-bl-full" />
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Inventory Value</p>
                      <p className={`text-3xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(dashboard?.inventory?.total_stock_value)}</p>
                      <p className="text-xs text-emerald-500 mt-2 flex items-center">
                        <Package className="w-3 h-3 mr-1" /> {dashboard?.inventory?.total_sku_count || 0} SKUs
                      </p>
                    </div>
                    <div className="p-3 rounded-xl bg-emerald-500/20">
                      <Package className="w-6 h-6 text-emerald-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className={`backdrop-blur-sm overflow-hidden relative ${isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-gray-200 shadow-sm'}`}>
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-cyan-500/20 to-transparent rounded-bl-full" />
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Total Revenue</p>
                      <p className={`text-3xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(dashboard?.financials?.total_revenue)}</p>
                      <p className="text-xs text-cyan-500 mt-2 flex items-center">
                        <TrendingUp className="w-3 h-3 mr-1" /> {formatCurrency(dashboard?.financials?.total_profit)} profit
                      </p>
                    </div>
                    <div className="p-3 rounded-xl bg-cyan-500/20">
                      <DollarSign className="w-6 h-6 text-cyan-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Financial Summary Bar */}
            <Card className={`${isDark ? 'bg-gradient-to-r from-slate-800/80 to-slate-800/50 border-slate-700/50' : 'bg-white border-gray-200 shadow-sm'}`}>
              <CardContent className="p-4 lg:p-6">
                <div className={`grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-0 sm:divide-x ${isDark ? 'sm:divide-slate-700' : 'sm:divide-gray-200'}`}>
                  <div className="sm:px-6 sm:first:pl-0 text-center sm:text-left">
                    <p className={`text-xs sm:text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Pending Invoices</p>
                    <p className="text-xl sm:text-2xl font-bold text-amber-500 mt-1">{formatCurrency(dashboard?.financials?.pending_revenue)}</p>
                  </div>
                  <div className="sm:px-6 text-center sm:text-left">
                    <p className={`text-xs sm:text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>In Transit Value</p>
                    <p className="text-xl sm:text-2xl font-bold text-blue-500 mt-1">{formatCurrency(dashboard?.inventory?.total_reserved_value)}</p>
                  </div>
                  <div className="sm:px-6 text-center sm:text-left">
                    <p className={`text-xs sm:text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Total Profit</p>
                    <p className="text-xl sm:text-2xl font-bold text-emerald-500 mt-1">{formatCurrency(dashboard?.financials?.total_profit)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Activity Grid */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Active Shipments */}
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-gray-200 shadow-sm'}`}>
                <CardHeader className="pb-3">
                  <CardTitle className={`flex items-center justify-between ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    <span className="flex items-center gap-2">
                      <Ship className="w-5 h-5 text-blue-500" /> Active Shipments
                    </span>
                    <Button size="sm" variant="ghost" onClick={() => setActiveTab("shipments")} className={isDark ? 'text-slate-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}>
                      View All <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {shipments.length > 0 ? (
                    <div className="space-y-3">
                      {shipments.slice(0, 4).map((s) => {
                        const config = STATUS_CONFIG[s.status] || STATUS_CONFIG.on_water;
                        return (
                          <div key={s.id} className={`p-4 rounded-xl transition-colors ${isDark ? 'bg-slate-700/30 hover:bg-slate-700/50' : 'bg-gray-50 hover:bg-gray-100'}`}>
                            <div className="flex items-start justify-between">
                              <div>
                                <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{s.container_number || s.bill_of_lading || 'Shipment'}</p>
                                <p className={`text-sm mt-0.5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{s.origin_country} → {s.final_destination || 'USA'}</p>
                                {s.estimated_arrival && (
                                  <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                                    <Clock className="w-3 h-3 inline mr-1" />
                                    ETA: {new Date(s.estimated_arrival).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                              <Badge className={`${config.bg} ${config.text} border-0`}>
                                {config.label}
                              </Badge>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Ship className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-gray-400'}`} />
                      <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>No active shipments</p>
                      <Button size="sm" onClick={() => setShowShipmentModal(true)} className="mt-3 bg-blue-500/20 text-blue-500 hover:bg-blue-500/30">
                        <Plus className="w-4 h-4 mr-1" /> Add Shipment
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Recent Invoices */}
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-gray-200 shadow-sm'}`}>
                <CardHeader className="pb-3">
                  <CardTitle className={`flex items-center justify-between ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    <span className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-cyan-500" /> Recent Invoices
                    </span>
                    <Button size="sm" variant="ghost" onClick={() => setActiveTab("invoices")} className={isDark ? 'text-slate-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}>
                      View All <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {invoices.length > 0 ? (
                    <div className="space-y-3">
                      {invoices.slice(0, 4).map((inv) => {
                        const config = STATUS_CONFIG[inv.payment_status] || STATUS_CONFIG.pending;
                        return (
                          <div key={inv.id} className={`p-4 rounded-xl transition-colors cursor-pointer ${isDark ? 'bg-slate-700/30 hover:bg-slate-700/50' : 'bg-gray-50 hover:bg-gray-100'}`} onClick={() => { setSelectedInvoice(inv); setShowInvoiceDetailModal(true); }}>
                            <div className="flex items-start justify-between">
                              <div>
                                <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{inv.invoice_number}</p>
                                <p className={`text-sm mt-0.5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{inv.consignee_company || inv.consignee_name}</p>
                              </div>
                              <div className="text-right">
                                <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(inv.total)}</p>
                                <Badge className={`${config.bg} ${config.text} border-0 mt-1`}>
                                  {config.label}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <FileText className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-gray-400'}`} />
                      <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>No invoices yet</p>
                      <Button size="sm" onClick={() => setShowInvoiceModal(true)} className="mt-3 bg-cyan-500/20 text-cyan-500 hover:bg-cyan-500/30">
                        <Plus className="w-4 h-4 mr-1" /> Create Invoice
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Shipments Tab */}
        {activeTab === "shipments" && (
          <div className="space-y-6" data-testid="shipments-content">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <h2 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Shipments</h2>
                <p className={`mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Track your containers from origin to warehouse</p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadTemplate('shipments')}
                  className={isDark ? 'border-slate-600 text-slate-200 hover:bg-slate-700' : ''}
                  data-testid="shipments-download-template-btn"
                  title="Download a CSV template with the correct column names"
                >
                  <Download className="w-4 h-4 mr-2" /> Template
                </Button>
                <label className={`cursor-pointer ${uploadingState.active ? 'pointer-events-none opacity-50' : ''}`}>
                  <input
                    type="file"
                    accept=".csv,.pdf,.doc,.docx,.xlsx,.xls,.jpg,.jpeg,.png,.webp"
                    multiple
                    className="hidden"
                    disabled={uploadingState.active}
                    onChange={async (e) => {
                      const files = Array.from(e.target.files || []);
                      if (files.length === 0) return;
                      
                      let created = 0;
                      let failed = 0;
                      const totalFiles = files.length;
                      
                      setUploadingState({ active: true, current: 0, total: totalFiles, filename: '' });
                      
                      for (let i = 0; i < files.length; i++) {
                        const file = files[i];
                        const formData = new FormData();
                        formData.append('file', file);
                        
                        setUploadingState({ active: true, current: i + 1, total: totalFiles, filename: file.name });
                        toast.loading(`Processing file ${i + 1} of ${totalFiles}: ${file.name}...`, { id: 'upload-progress' });
                        
                        try {
                          await axios.post(`${API}/b/${businessSlug}/wholesale/shipments/smart-upload`, formData, {
                            headers: { 'Content-Type': 'multipart/form-data' },
                            timeout: 120000 // 2 minute timeout
                          });
                          created++;
                          toast.success(`✓ ${file.name} processed successfully`, { duration: 3000 });
                        } catch (error) {
                          failed++;
                          const errorDetail = error.response?.data?.detail || '';
                          const isAIError = errorDetail.includes('AI service') || errorDetail.includes('502') || errorDetail.includes('temporarily unavailable') || error.code === 'ECONNABORTED';
                          const errorMsg = isAIError 
                            ? `AI service temporarily unavailable for "${file.name}". Please try again in a moment.`
                            : `Failed to process "${file.name}": ${errorDetail || 'Unknown error'}`;
                          toast.error(errorMsg, { duration: 6000 });
                        }
                      }
                      
                      toast.dismiss('upload-progress');
                      setUploadingState({ active: false, current: 0, total: 0, filename: '' });
                      
                      if (created > 0) {
                        toast.success(`Upload complete! Created ${created} shipment(s)${failed > 0 ? `, ${failed} failed` : ''}`, { duration: 5000 });
                        fetchAllData();
                      } else if (failed > 0) {
                        toast.error(`All ${failed} file(s) failed to process. The AI service may be temporarily unavailable.`, { duration: 6000 });
                      }
                      e.target.value = '';
                    }}
                  />
                  <span className={`inline-flex items-center px-4 py-2 rounded-md transition-colors ${isDark ? 'bg-slate-700/50 hover:bg-slate-700 border border-slate-600 text-white' : 'bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-700'}`}>
                    {uploadingState.active ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Processing...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" /> Upload Shipment
                      </>
                    )}
                  </span>
                </label>
                <Button onClick={() => setShowShipmentModal(true)} className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white shadow-lg shadow-cyan-500/25">
                  <Plus className="w-4 h-4 mr-2" /> New Shipment
                </Button>
              </div>
            </div>
            
            {/* Status Summary */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { status: "on_water", count: shipments.filter(s => s.status === "on_water").length },
                { status: "at_port", count: shipments.filter(s => s.status === "at_port" || s.status === "customs_hold").length },
                { status: "in_transit", count: shipments.filter(s => s.status === "in_transit").length },
                { status: "received", count: shipments.filter(s => s.status === "received").length },
              ].map((item) => {
                const config = STATUS_CONFIG[item.status];
                const Icon = config.icon;
                return (
                  <Card key={item.status} className={`transition-colors cursor-pointer ${isDark ? 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600' : 'bg-white border-gray-200 hover:border-gray-300 shadow-sm'}`}>
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className={`p-3 rounded-xl ${config.bg}`}>
                        <Icon className={`w-6 h-6 ${config.text}`} />
                      </div>
                      <div>
                        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{config.label}</p>
                        <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.count}</p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            
            {/* Shipments List */}
            <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-gray-200 shadow-sm'}`}>
              <CardContent className="p-0">
                {shipments.length > 0 ? (
                  <div className={`divide-y ${isDark ? 'divide-slate-700/50' : 'divide-gray-200'}`}>
                    {shipments.map((shipment) => {
                      const config = STATUS_CONFIG[shipment.status] || STATUS_CONFIG.on_water;
                      const Icon = config.icon;
                      return (
                        <div 
                          key={shipment.id} 
                          className={`p-5 transition-colors cursor-pointer ${isDark ? 'hover:bg-slate-700/20' : 'hover:bg-gray-50'}`}
                          onClick={() => handleEditShipment(shipment)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-4">
                              <div className={`p-3 rounded-xl ${config.bg}`}>
                                <Icon className={`w-6 h-6 ${config.text}`} />
                              </div>
                              <div>
                                <p className="font-semibold text-white text-lg">{shipment.container_number || shipment.bill_of_lading || 'Shipment'}</p>
                                <p className="text-slate-400 text-sm mt-1">
                                  BOL: {shipment.bill_of_lading || 'N/A'} • Customs: {shipment.customs_entry_number || 'N/A'}
                                </p>
                                <div className="flex flex-wrap gap-4 mt-3">
                                  <span className="text-sm text-slate-500 flex items-center">
                                    <MapPin className="w-4 h-4 mr-1" />
                                    {shipment.origin_country || 'Origin'} → {shipment.final_destination || 'USA'}
                                  </span>
                                  {shipment.estimated_arrival && (
                                    <span className="text-sm text-slate-500 flex items-center">
                                      <Clock className="w-4 h-4 mr-1" />
                                      ETA: {new Date(shipment.estimated_arrival).toLocaleDateString()}
                                    </span>
                                  )}
                                  <span className="text-sm text-slate-500 flex items-center">
                                    <Box className="w-4 h-4 mr-1" />
                                    {shipment.total_cartons || 0} cartons / {shipment.total_units || 0} units
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                              <Badge className={`${config.bg} ${config.text} border-0 px-3 py-1`}>
                                {config.label}
                              </Badge>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white">
                                    <MoreVertical className="w-5 h-5" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
                                  <DropdownMenuItem
                                    onClick={() => window.open(`/${businessSlug}/wholesale/shipments/${shipment.id}/print`, '_blank')}
                                    className="text-slate-300 hover:bg-slate-700"
                                    data-testid={`view-shipment-document-${shipment.id}`}
                                  >
                                    <FileText className="w-4 h-4 mr-2 text-cyan-400" /> View Document
                                  </DropdownMenuItem>
                                  {shipment.status === "on_water" && (
                                    <DropdownMenuItem onClick={() => handleUpdateShipmentStatus(shipment.id, "at_port")} className="text-slate-300 hover:bg-slate-700">
                                      <Anchor className="w-4 h-4 mr-2 text-amber-400" /> Mark At Port
                                    </DropdownMenuItem>
                                  )}
                                  {(shipment.status === "at_port" || shipment.status === "customs_hold") && (
                                    <DropdownMenuItem onClick={() => handleUpdateShipmentStatus(shipment.id, "in_transit")} className="text-slate-300 hover:bg-slate-700">
                                      <Truck className="w-4 h-4 mr-2 text-purple-400" /> Mark In Transit
                                    </DropdownMenuItem>
                                  )}
                                  {shipment.status === "in_transit" && (
                                    <DropdownMenuItem onClick={() => handleUpdateShipmentStatus(shipment.id, "received")} className="text-slate-300 hover:bg-slate-700">
                                      <CheckCircle className="w-4 h-4 mr-2 text-emerald-400" /> Mark Received
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem 
                                    onClick={() => setShowDeleteShipmentConfirm(shipment)} 
                                    className="text-red-400 hover:bg-red-500/20 hover:text-red-300"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" /> Delete Shipment
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-12 text-center">
                    <Ship className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400 text-lg">No shipments yet</p>
                    <p className="text-slate-500 text-sm mt-1">Create your first shipment to start tracking</p>
                    <Button onClick={() => setShowShipmentModal(true)} className="mt-6 bg-gradient-to-r from-cyan-500 to-blue-600">
                      <Plus className="w-4 h-4 mr-2" /> Create First Shipment
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Inventory Tab */}
        {activeTab === "inventory" && (() => {
          // Get unique countries from products
          // Normalize country for filtering (handle Turkey/Türkiye variants)
          const normalizeCountry = (country) => {
            if (!country) return '';
            const lower = country.toLowerCase().trim();
            if (lower === 'turkey' || lower === 'türkiye' || lower === 'turkiye') return 'Turkey';
            return country;
          };
          
          // Get unique countries, normalizing Turkey variants
          const rawCountries = products.map(p => normalizeCountry(p.country_of_origin)).filter(Boolean);
          const defaultCountries = ["Turkey", "Dubai", "China"];
          const allCountries = [...new Set([...defaultCountries, ...rawCountries])].sort();
          
          // Filter products by selected country (case-insensitive, with Turkey normalization)
          const filteredProducts = selectedCountry === "all" 
            ? products 
            : products.filter(p => normalizeCountry(p.country_of_origin) === normalizeCountry(selectedCountry));
          
          // Calculate stats for filtered products
          const filteredStats = {
            count: filteredProducts.length,
            units: filteredProducts.reduce((sum, p) => sum + (p.stock_quantity || 0), 0),
            value: filteredProducts.reduce((sum, p) => sum + ((p.purchase_price || p.fob_price || 0) * (p.stock_quantity || 0)), 0),
            revenue: filteredProducts.reduce((sum, p) => sum + ((p.sell_price || 0) * (p.stock_quantity || 0)), 0)
          };
          
          return (
          <div className="space-y-6" data-testid="inventory-content">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <h2 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Inventory</h2>
                <p className={`mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Manage products with landed costs and margins</p>
              </div>
              <div className="flex gap-2 items-center">
                {/* File type helper text */}
                <span className={`text-xs hidden lg:inline ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  CSV • Excel • PDF • Word • Images
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadTemplate('products')}
                  className={isDark ? 'border-slate-600 text-slate-200 hover:bg-slate-700' : ''}
                  data-testid="inventory-download-template-btn"
                  title="Download a CSV template with the correct column names"
                >
                  <Download className="w-4 h-4 mr-2" /> Template
                </Button>
                <label className={`cursor-pointer relative group ${uploadingState.active ? 'pointer-events-none opacity-50' : ''}`} title="Upload CSV, Excel, PDF, Word documents or Images. AI will automatically extract product data.">
                  <input
                    type="file"
                    accept=".csv,.pdf,.doc,.docx,.xlsx,.xls,.jpg,.jpeg,.png,.webp"
                    multiple
                    className="hidden"
                    disabled={uploadingState.active}
                    data-testid="inventory-file-upload"
                    onChange={async (e) => {
                      const files = Array.from(e.target.files || []);
                      if (files.length === 0) return;
                      
                      let totalAdded = 0;
                      let totalSkipped = 0;
                      let failed = 0;
                      const totalFiles = files.length;
                      
                      setUploadingState({ active: true, current: 0, total: totalFiles, filename: '' });
                      
                      for (let i = 0; i < files.length; i++) {
                        const file = files[i];
                        const formData = new FormData();
                        formData.append('file', file);
                        
                        // Use bulk-upload for CSV (fast, reliable), smart-upload for other formats (AI)
                        const isCSV = file.name.toLowerCase().endsWith('.csv');
                        const endpoint = isCSV 
                          ? `${API}/b/${businessSlug}/wholesale/products/bulk-upload`
                          : `${API}/b/${businessSlug}/wholesale/products/smart-upload`;
                        
                        setUploadingState({ active: true, current: i + 1, total: totalFiles, filename: file.name });
                        toast.loading(`Processing file ${i + 1} of ${totalFiles}: ${file.name}...`, { id: 'upload-progress' });
                        
                        try {
                          const response = await axios.post(endpoint, formData, {
                            headers: { 'Content-Type': 'multipart/form-data' },
                            timeout: 120000 // 2 minute timeout
                          });
                          totalAdded += response.data.added || 0;
                          totalSkipped += response.data.skipped || 0;
                          toast.success(`✓ ${file.name}: ${response.data.added || 0} products added`, { duration: 3000 });
                        } catch (error) {
                          failed++;
                          const errorDetail = error.response?.data?.detail || '';
                          const isAIError = errorDetail.includes('AI service') || errorDetail.includes('502') || errorDetail.includes('temporarily unavailable') || error.code === 'ECONNABORTED';
                          const errorMsg = isAIError 
                            ? `AI service temporarily unavailable for "${file.name}". Please try again in a moment.`
                            : `Failed to process "${file.name}": ${errorDetail || 'Unknown error'}`;
                          toast.error(errorMsg, { duration: 6000 });
                        }
                      }
                      
                      toast.dismiss('upload-progress');
                      setUploadingState({ active: false, current: 0, total: 0, filename: '' });
                      
                      if (totalAdded > 0 || totalSkipped > 0) {
                        toast.success(`Upload complete! Added ${totalAdded} products${totalSkipped ? `, ${totalSkipped} skipped (duplicates)` : ''}${failed > 0 ? `, ${failed} file(s) failed` : ''}`, { duration: 5000 });
                        fetchAllData();
                      } else if (failed > 0) {
                        toast.error(`All ${failed} file(s) failed to process. The AI service may be temporarily unavailable.`, { duration: 6000 });
                      }
                      e.target.value = '';
                    }}
                  />
                  <span className={`inline-flex items-center px-4 py-2 rounded-md transition-colors ${isDark ? 'bg-slate-700/50 hover:bg-slate-700 border border-slate-600 text-white' : 'bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-700'}`}>
                    {uploadingState.active ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> 
                        {uploadingState.total > 1 
                          ? `File ${uploadingState.current}/${uploadingState.total}...`
                          : 'Processing...'}
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" /> Upload File
                      </>
                    )}
                  </span>
                  {/* Tooltip on hover */}
                  <div className={`absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50 border ${isDark ? 'bg-slate-900 text-slate-300 border-slate-700' : 'bg-white text-gray-600 border-gray-200'}`}>
                    <div className={`font-medium mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>Accepted formats:</div>
                    <div>CSV, Excel (.xlsx, .xls), PDF, Word (.doc, .docx), Images</div>
                    <div className="text-cyan-500 mt-1">AI extracts product data automatically</div>
                    <div className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 border-r border-b ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}></div>
                  </div>
                </label>
                <Button onClick={() => setShowProductModal(true)} className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white shadow-lg shadow-cyan-500/25">
                  <Plus className="w-4 h-4 mr-2" /> Add Product
                </Button>
              </div>
            </div>

            {/* Country Filter Tabs */}
            <div className="flex flex-wrap gap-2">
              <Button
                variant={selectedCountry === "all" ? "default" : "outline"}
                onClick={() => { setSelectedCountry("all"); setSelectedProductIds([]); }}
                className={selectedCountry === "all" 
                  ? "bg-gradient-to-r from-cyan-500 to-blue-600 text-white" 
                  : isDark ? "border-slate-600 text-slate-300 hover:bg-slate-700" : "border-gray-300 text-gray-600 hover:bg-gray-100"}
                data-testid="country-filter-all"
              >
                <Globe className="w-4 h-4 mr-2" />
                All Countries
                <Badge className={`ml-2 ${isDark ? 'bg-slate-700/50 text-slate-300' : 'bg-gray-200 text-gray-600'}`}>{products.length}</Badge>
              </Button>
              {allCountries.map(country => {
                const countryCount = products.filter(p => normalizeCountry(p.country_of_origin) === normalizeCountry(country)).length;
                const isSelected = selectedCountry === country;
                // Country flag colors
                const countryColors = {
                  "Turkey": "from-red-500 to-red-600",
                  "Dubai": "from-green-500 to-green-600", 
                  "China": "from-yellow-500 to-red-500",
                  "USA": "from-blue-500 to-red-500",
                };
                const colorClass = countryColors[country] || "from-slate-500 to-slate-600";
                
                return (
                  <Button
                    key={country}
                    variant={isSelected ? "default" : "outline"}
                    onClick={() => { setSelectedCountry(country); setSelectedProductIds([]); }}
                    className={isSelected 
                      ? `bg-gradient-to-r ${colorClass} text-white` 
                      : isDark ? "border-slate-600 text-slate-300 hover:bg-slate-700" : "border-gray-300 text-gray-600 hover:bg-gray-100"}
                    data-testid={`country-filter-${country.toLowerCase()}`}
                  >
                    {country}
                    <Badge className={`ml-2 ${isSelected ? 'bg-white/20 text-white' : isDark ? 'bg-slate-700/50 text-slate-300' : 'bg-gray-200 text-gray-600'}`}>
                      {countryCount}
                    </Badge>
                  </Button>
                );
              })}
            </div>

            {/* Inventory Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className={isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-gray-200 shadow-sm'}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-cyan-500/20">
                      <Package className="w-5 h-5 text-cyan-500" />
                    </div>
                    <div>
                      <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{filteredStats.count}</p>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Total Products</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className={isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-gray-200 shadow-sm'}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-emerald-500/20">
                      <Box className="w-5 h-5 text-emerald-500" />
                    </div>
                    <div>
                      <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{filteredStats.units.toLocaleString()}</p>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Total Units in Stock</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className={isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-gray-200 shadow-sm'}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-amber-500/20">
                      <DollarSign className="w-5 h-5 text-amber-500" />
                    </div>
                    <div>
                      <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(filteredStats.value)}</p>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Inventory Value (Cost)</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className={isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-gray-200 shadow-sm'}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-purple-500/20">
                      <TrendingUp className="w-5 h-5 text-purple-500" />
                    </div>
                    <div>
                      <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(filteredStats.revenue)}</p>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Potential Revenue</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Bulk Actions Bar */}
            {selectedProductIds.length > 0 && (
              <div className="flex items-center justify-between p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className={isDark ? 'text-white' : 'text-gray-900'}>
                  <span className="font-bold text-red-500">{selectedProductIds.length}</span> product{selectedProductIds.length > 1 ? 's' : ''} selected
                </p>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setSelectedProductIds([])}
                    className={isDark ? "border-slate-600 text-slate-300 hover:bg-slate-700" : "border-gray-300 text-gray-600 hover:bg-gray-100"}
                  >
                    Clear Selection
                  </Button>
                  <Button 
                    onClick={() => setShowBulkDeleteConfirm(true)}
                    className="bg-red-500 hover:bg-red-600 text-white"
                    data-testid="bulk-delete-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Delete Selected
                  </Button>
                </div>
              </div>
            )}
            
            <Card className={`overflow-hidden ${isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-gray-200 shadow-sm'}`}>
              <CardContent className="p-0">
                {filteredProducts.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className={`border-b ${isDark ? 'bg-slate-700/30 border-slate-700/50' : 'bg-gray-50 border-gray-200'}`}>
                          <th className="p-4 w-12">
                            <input
                              type="checkbox"
                              checked={selectedProductIds.length === filteredProducts.length && filteredProducts.length > 0}
                              onChange={() => {
                                if (selectedProductIds.length === filteredProducts.length) {
                                  setSelectedProductIds([]);
                                } else {
                                  setSelectedProductIds(filteredProducts.map(p => p.id));
                                }
                              }}
                              className={`w-4 h-4 rounded text-cyan-500 focus:ring-cyan-500 cursor-pointer ${isDark ? 'border-slate-500 bg-slate-700' : 'border-gray-300 bg-white'}`}
                              data-testid="select-all-checkbox"
                            />
                          </th>
                          <th className={`text-center p-4 text-xs font-semibold uppercase tracking-wider w-12 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>#</th>
                          <th className={`text-left p-4 text-xs font-semibold uppercase tracking-wider w-16 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Image</th>
                          <th className={`text-left p-4 text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Product Name</th>
                          <th className={`text-left p-4 text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Details</th>
                          <th className={`text-right p-4 text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Purchase</th>
                          <th className={`text-right p-4 text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Sell Price</th>
                          <th className={`text-right p-4 text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Profit %</th>
                          <th className={`text-right p-4 text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Stock</th>
                          <th className={`text-center p-4 text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Actions</th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${isDark ? 'divide-slate-700/30' : 'divide-gray-100'}`}>
                        {filteredProducts.map((product, index) => (
                          <tr key={product.id} className={`transition-colors ${selectedProductIds.includes(product.id) ? 'bg-cyan-500/10' : ''} ${isDark ? 'hover:bg-slate-700/20' : 'hover:bg-gray-50'}`}>
                            <td className="p-4">
                              <input
                                type="checkbox"
                                checked={selectedProductIds.includes(product.id)}
                                onChange={() => toggleProductSelection(product.id)}
                                className={`w-4 h-4 rounded text-cyan-500 focus:ring-cyan-500 cursor-pointer ${isDark ? 'border-slate-500 bg-slate-700' : 'border-gray-300 bg-white'}`}
                                data-testid={`select-product-${product.id}`}
                              />
                            </td>
                            <td className={`p-4 text-center font-mono text-sm ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>{index + 1}</td>
                            <td className="p-4">
                              {product.image_url ? (
                                <div className="relative group">
                                  <img 
                                    src={product.image_url} 
                                    alt={product.name} 
                                    className={`w-12 h-12 object-cover rounded border cursor-pointer hover:opacity-80 hover:border-cyan-500 transition-all ${isDark ? 'border-slate-600' : 'border-gray-300'}`}
                                    onClick={() => setPreviewImage({ url: product.image_url, name: product.name, sku: product.sku })}
                                    data-testid={`product-image-${product.id}`}
                                  />
                                  {/* Overlay with change/remove buttons on hover */}
                                  <div className="absolute inset-0 bg-black/60 rounded opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                                    <label className="cursor-pointer p-1 rounded bg-cyan-500/80 hover:bg-cyan-500 transition-colors" title="Change image">
                                      <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={async (e) => {
                                          const file = e.target.files?.[0];
                                          if (!file) return;
                                          if (file.size > 5 * 1024 * 1024) {
                                            toast.error('Image too large. Please use an image under 5MB.');
                                            return;
                                          }
                                          const reader = new FileReader();
                                          reader.onload = async (event) => {
                                            const base64 = event.target?.result;
                                            try {
                                              await axios.put(`${API}/b/${businessSlug}/wholesale/products/${product.id}`, {
                                                image_url: base64
                                              });
                                              toast.success('Image updated!');
                                              fetchAllData();
                                            } catch (error) {
                                              console.error('Image upload error:', error);
                                              toast.error('Failed to update image');
                                            }
                                          };
                                          reader.readAsDataURL(file);
                                        }}
                                      />
                                      <Camera className="w-3 h-3 text-white" />
                                    </label>
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        try {
                                          await axios.put(`${API}/b/${businessSlug}/wholesale/products/${product.id}`, {
                                            image_url: ""
                                          });
                                          toast.success('Image removed!');
                                          fetchAllData();
                                        } catch (error) {
                                          console.error('Image remove error:', error);
                                          toast.error('Failed to remove image');
                                        }
                                      }}
                                      className="p-1 rounded bg-red-500/80 hover:bg-red-500 transition-colors"
                                      title="Remove image"
                                    >
                                      <X className="w-3 h-3 text-white" />
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <label className="cursor-pointer group">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={async (e) => {
                                      const file = e.target.files?.[0];
                                      if (!file) return;
                                      if (file.size > 5 * 1024 * 1024) {
                                        toast.error('Image too large. Please use an image under 5MB.');
                                        return;
                                      }
                                      // Convert to base64
                                      const reader = new FileReader();
                                      reader.onload = async (event) => {
                                        const base64 = event.target?.result;
                                        try {
                                          // Update product with ONLY the image_url field to avoid validation issues
                                          await axios.put(`${API}/b/${businessSlug}/wholesale/products/${product.id}`, {
                                            image_url: base64
                                          });
                                          toast.success('Image uploaded successfully!');
                                          fetchAllData();
                                        } catch (error) {
                                          console.error('Image upload error:', error);
                                          toast.error('Failed to upload image');
                                        }
                                      };
                                      reader.readAsDataURL(file);
                                    }}
                                  />
                                  <div className={`w-12 h-12 rounded border border-dashed flex items-center justify-center group-hover:border-cyan-500 transition-all ${isDark ? 'bg-slate-700 border-slate-600 group-hover:bg-slate-600' : 'bg-gray-100 border-gray-300 group-hover:bg-gray-200'}`} title="Click to upload image">
                                    <Camera className={`w-5 h-5 group-hover:text-cyan-500 transition-colors ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
                                  </div>
                                </label>
                              )}
                            </td>
                            <td className="p-4">
                              <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{product.name}</p>
                              <p className="text-xs font-mono text-cyan-500 mt-0.5">{product.sku}</p>
                            </td>
                            <td className="p-4">
                              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                                {product.category || 'No category'}
                                {product.country_of_origin && (
                                  <span className="text-cyan-500 ml-1">• {product.country_of_origin}</span>
                                )}
                              </p>
                              <p className={`text-xs mt-0.5 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                                {product.units_per_carton || 1} per {product.carton_type || 'carton'}
                              </p>
                            </td>
                            <td className={`p-4 text-right ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>{formatCurrency(product.purchase_price || product.fob_price)}</td>
                            <td className={`p-4 text-right font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(product.sell_price)}</td>
                            <td className="p-4 text-right">
                              {(() => {
                                const cost = product.purchase_price || product.fob_price || 0;
                                const sell = product.sell_price || 0;
                                const profit = sell - cost;
                                const profitPercent = cost > 0 ? ((profit / cost) * 100) : 0;
                                return (
                                  <div>
                                    <Badge className={`${profitPercent >= 30 ? 'bg-emerald-500/20 text-emerald-500' : profitPercent >= 15 ? 'bg-amber-500/20 text-amber-500' : 'bg-red-500/20 text-red-500'} border-0`}>
                                      {profitPercent.toFixed(1)}%
                                    </Badge>
                                    <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>{formatCurrency(profit)}</p>
                                  </div>
                                );
                              })()}
                            </td>
                            <td className={`p-4 text-right font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{product.stock_quantity || 0}</td>
                            <td className="p-4 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleEditProduct(product)}
                                  className="h-8 w-8 p-0 hover:bg-cyan-500/20 text-cyan-500"
                                  data-testid={`edit-product-${product.id}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setShowDeleteProductConfirm(product)}
                                  className="h-8 w-8 p-0 hover:bg-red-500/20 text-red-500"
                                  data-testid={`delete-product-${product.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-12 text-center">
                    <Package className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400 text-lg">{selectedCountry === "all" ? "No products yet" : `No products from ${selectedCountry}`}</p>
                    <Button onClick={() => setShowProductModal(true)} className="mt-6 bg-gradient-to-r from-cyan-500 to-blue-600">
                      <Plus className="w-4 h-4 mr-2" /> Add First Product
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          );
        })()}

        {/* Invoices Tab */}
        {activeTab === "invoices" && (
          <div className="space-y-4 lg:space-y-6" data-testid="invoices-content">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 lg:gap-4">
              <div>
                <h2 className="text-2xl lg:text-3xl font-bold text-white">Invoices</h2>
                <p className="text-slate-400 text-sm lg:text-base mt-1">Create and manage wholesale invoices</p>
              </div>
              <div className="flex gap-2 items-center">
                <span className="text-xs text-slate-400 hidden lg:inline">
                  CSV • Excel • PDF • Word • Images
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadTemplate('invoices')}
                  className={isDark ? 'border-slate-600 text-slate-200 hover:bg-slate-700' : ''}
                  data-testid="invoices-download-template-btn"
                  title="Download a CSV template with the correct column names"
                >
                  <Download className="w-4 h-4 mr-2" /> Template
                </Button>
                <label className={`cursor-pointer relative group ${uploadingState.active ? 'pointer-events-none opacity-50' : ''}`} title="Upload invoice documents. AI will extract invoice data.">
                  <input
                    type="file"
                    accept=".csv,.pdf,.doc,.docx,.xlsx,.xls,.jpg,.jpeg,.png,.webp"
                    multiple
                    className="hidden"
                    disabled={uploadingState.active}
                    data-testid="invoice-file-upload"
                    onChange={async (e) => {
                      const files = Array.from(e.target.files || []);
                      if (files.length === 0) return;
                      
                      let created = 0;
                      let failed = 0;
                      const totalFiles = files.length;
                      
                      setUploadingState({ active: true, current: 0, total: totalFiles, filename: '' });
                      
                      for (let i = 0; i < files.length; i++) {
                        const file = files[i];
                        const formData = new FormData();
                        formData.append('file', file);
                        
                        setUploadingState({ active: true, current: i + 1, total: totalFiles, filename: file.name });
                        toast.loading(`Processing file ${i + 1} of ${totalFiles}: ${file.name}...`, { id: 'upload-progress' });
                        
                        try {
                          await axios.post(`${API}/b/${businessSlug}/wholesale/invoices/smart-upload`, formData, {
                            headers: { 'Content-Type': 'multipart/form-data' },
                            timeout: 120000 // 2 minute timeout
                          });
                          created++;
                          toast.success(`✓ ${file.name} processed successfully`, { duration: 3000 });
                        } catch (error) {
                          failed++;
                          const errorDetail = error.response?.data?.detail || '';
                          const isAIError = errorDetail.includes('AI service') || errorDetail.includes('502') || errorDetail.includes('temporarily unavailable') || error.code === 'ECONNABORTED';
                          const errorMsg = isAIError 
                            ? `AI service temporarily unavailable for "${file.name}". Please try again in a moment.`
                            : `Failed to process "${file.name}": ${errorDetail || 'Unknown error'}`;
                          toast.error(errorMsg, { duration: 6000 });
                        }
                      }
                      
                      toast.dismiss('upload-progress');
                      setUploadingState({ active: false, current: 0, total: 0, filename: '' });
                      
                      if (created > 0) {
                        toast.success(`Upload complete! Created ${created} invoice(s)${failed > 0 ? `, ${failed} failed` : ''}`, { duration: 5000 });
                        fetchAllData();
                      } else if (failed > 0) {
                        toast.error(`All ${failed} file(s) failed to process. The AI service may be temporarily unavailable.`, { duration: 6000 });
                      }
                      e.target.value = '';
                    }}
                  />
                  <span className="inline-flex items-center px-4 py-2 bg-slate-700/50 hover:bg-slate-700 border border-slate-600 text-white rounded-md transition-colors">
                    {uploadingState.active ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> 
                        {uploadingState.total > 1 
                          ? `File ${uploadingState.current}/${uploadingState.total}...`
                          : 'Processing...'}
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" /> Upload Invoice
                      </>
                    )}
                  </span>
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-slate-900 text-xs text-slate-300 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50 border border-slate-700">
                    <div className="font-medium text-white mb-1">Accepted formats:</div>
                    <div>CSV, Excel, PDF, Word, Images</div>
                    <div className="text-cyan-400 mt-1">AI extracts invoice data automatically</div>
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-slate-900 border-r border-b border-slate-700"></div>
                  </div>
                </label>
                <Button onClick={() => setShowInvoiceModal(true)} className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white shadow-lg shadow-cyan-500/25 w-full sm:w-auto">
                  <Plus className="w-4 h-4 mr-2" /> New Invoice
                </Button>
              </div>
            </div>
            
            {/* Customer Filter Tabs */}
            {(() => {
              const customers = [...new Set(invoices.map(inv => inv.customer_name || inv.company_name || 'Unknown'))].sort();
              const filteredInvoices = selectedCustomer === 'all' 
                ? invoices 
                : invoices.filter(inv => (inv.customer_name || inv.company_name || 'Unknown') === selectedCustomer);
              
              // Calculate totals for filtered invoices
              const totalDue = filteredInvoices.filter(i => i.payment_status === 'pending' || i.payment_status === 'partial').reduce((sum, i) => sum + (i.total || 0), 0);
              const totalPaid = filteredInvoices.filter(i => i.payment_status === 'paid').reduce((sum, i) => sum + (i.total || 0), 0);
              
              return (
                <>
                  {customers.length > 1 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      <button
                        onClick={() => setSelectedCustomer('all')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          selectedCustomer === 'all'
                            ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/25'
                            : 'bg-slate-800/50 text-slate-300 border border-slate-700 hover:bg-slate-700/50'
                        }`}
                      >
                        All Customers ({invoices.length})
                      </button>
                      {customers.map((customer) => {
                        const count = invoices.filter(inv => (inv.customer_name || inv.company_name || 'Unknown') === customer).length;
                        return (
                          <button
                            key={customer}
                            onClick={() => setSelectedCustomer(customer)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                              selectedCustomer === customer
                                ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/25'
                                : 'bg-slate-800/50 text-slate-300 border border-slate-700 hover:bg-slate-700/50'
                            }`}
                          >
                            {customer} ({count})
                          </button>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Summary Cards for Selected Customer */}
                  {selectedCustomer !== 'all' && (
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                      <Card className="bg-slate-800/50 border-slate-700/50">
                        <CardContent className="p-4">
                          <p className="text-xs text-slate-400">Total Invoices</p>
                          <p className="text-xl font-bold text-white">{filteredInvoices.length}</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-slate-800/50 border-slate-700/50">
                        <CardContent className="p-4">
                          <p className="text-xs text-slate-400">Amount Due</p>
                          <p className="text-xl font-bold text-orange-400">${totalDue.toLocaleString()}</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-slate-800/50 border-slate-700/50">
                        <CardContent className="p-4">
                          <p className="text-xs text-slate-400">Amount Paid</p>
                          <p className="text-xl font-bold text-green-400">${totalPaid.toLocaleString()}</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-slate-800/50 border-slate-700/50">
                        <CardContent className="p-4">
                          <p className="text-xs text-slate-400">Total Value</p>
                          <p className="text-xl font-bold text-cyan-400">${(totalDue + totalPaid).toLocaleString()}</p>
                        </CardContent>
                      </Card>
                    </div>
                  )}
            
                  <Card className="bg-slate-800/50 border-slate-700/50">
                    <CardContent className="p-0">
                      {filteredInvoices.length > 0 ? (
                        <div className="divide-y divide-slate-700/50">
                          {filteredInvoices.map((invoice) => {
                            const config = STATUS_CONFIG[invoice.payment_status] || STATUS_CONFIG.pending;
                            return (
                              <div key={invoice.id} className="p-4 lg:p-5 hover:bg-slate-700/20 transition-colors cursor-pointer" onClick={() => { setSelectedInvoice(invoice); setShowInvoiceDetailModal(true); }}>
                          <div className="flex items-start lg:items-center justify-between gap-3">
                            {/* Left side: Icon + Invoice Info */}
                            <div className="flex items-start lg:items-center gap-3 lg:gap-4 flex-1 min-w-0">
                              <div className="p-2.5 lg:p-3 rounded-xl bg-cyan-500/20 shrink-0">
                                <FileText className="w-5 h-5 lg:w-6 lg:h-6 text-cyan-400" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="font-semibold text-white text-base lg:text-lg">{invoice.invoice_number}</p>
                                <p className="text-slate-400 text-sm truncate">Consignee: {invoice.consignee_company || invoice.consignee_name}</p>
                                <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1 text-xs text-slate-500">
                                  <span>Terms: {invoice.payment_terms?.replace('_', ' ').toUpperCase()}</span>
                                  {invoice.due_date && <span>Due: {new Date(invoice.due_date).toLocaleDateString()}</span>}
                                </div>
                              </div>
                            </div>
                            {/* Right side: Amount + Status */}
                            <div className="text-right shrink-0 flex items-center gap-2">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(`/${businessSlug}/wholesale/invoices/${invoice.id}/print`, '_blank');
                                }}
                                className="p-2 rounded-lg text-cyan-400 hover:bg-cyan-500/20 transition-colors"
                                title="View Document"
                                data-testid={`view-invoice-document-${invoice.id}`}
                              >
                                <FileText className="w-4 h-4" />
                              </button>
                              <div>
                                <p className="text-xl lg:text-2xl font-bold text-white">{formatCurrency(invoice.total)}</p>
                                <Badge className={`${config.bg} ${config.text} border-0 mt-1`}>{config.label}</Badge>
                                {invoice.total_profit > 0 && (
                                  <p className="text-xs text-emerald-400 mt-1">
                                    Profit: {formatCurrency(invoice.total_profit)} ({invoice.total_margin?.toFixed(1)}%)
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-12 text-center">
                    <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400 text-lg">No invoices yet</p>
                    <Button onClick={() => setShowInvoiceModal(true)} className="mt-6 bg-gradient-to-r from-cyan-500 to-blue-600">
                      <Plus className="w-4 h-4 mr-2" /> Create First Invoice
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
                </>
              );
            })()}
          </div>
        )}

        {/* Calculator Tab */}
        {activeTab === "calculator" && (
          <div className="space-y-6" data-testid="calculator-content">
            <div>
              <h2 className="text-3xl font-bold text-white">Landed Cost Calculator</h2>
              <p className="text-slate-400 mt-1">Calculate your true unit cost including all import expenses</p>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Input Form */}
              <Card className="bg-slate-800/50 border-slate-700/50">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-cyan-400" /> Cost Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">FOB Price (Total)</Label>
                      <Input
                        type="number"
                        value={landedCostForm.fob_price}
                        onChange={(e) => setLandedCostForm({...landedCostForm, fob_price: parseFloat(e.target.value) || 0})}
                        className="bg-slate-700/50 border-slate-600 text-white mt-1"
                        placeholder="$0.00"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Total Units</Label>
                      <Input
                        type="number"
                        value={landedCostForm.total_units}
                        onChange={(e) => setLandedCostForm({...landedCostForm, total_units: parseInt(e.target.value) || 1})}
                        className="bg-slate-700/50 border-slate-600 text-white mt-1"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-slate-300">Freight (Ocean/Air)</Label>
                    <Input
                      type="number"
                      value={landedCostForm.freight}
                      onChange={(e) => setLandedCostForm({...landedCostForm, freight: parseFloat(e.target.value) || 0})}
                      className="bg-slate-700/50 border-slate-600 text-white mt-1"
                      placeholder="$0.00"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-slate-300">Duty/Tariffs (Customs)</Label>
                    <Input
                      type="number"
                      value={landedCostForm.duty_tariff}
                      onChange={(e) => setLandedCostForm({...landedCostForm, duty_tariff: parseFloat(e.target.value) || 0})}
                      className="bg-slate-700/50 border-slate-600 text-white mt-1"
                      placeholder="$0.00"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-slate-300">Drayage (Port → Warehouse)</Label>
                    <Input
                      type="number"
                      value={landedCostForm.drayage}
                      onChange={(e) => setLandedCostForm({...landedCostForm, drayage: parseFloat(e.target.value) || 0})}
                      className="bg-slate-700/50 border-slate-600 text-white mt-1"
                      placeholder="$0.00"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Insurance</Label>
                      <Input
                        type="number"
                        value={landedCostForm.insurance}
                        onChange={(e) => setLandedCostForm({...landedCostForm, insurance: parseFloat(e.target.value) || 0})}
                        className="bg-slate-700/50 border-slate-600 text-white mt-1"
                        placeholder="$0.00"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Other Fees</Label>
                      <Input
                        type="number"
                        value={landedCostForm.other_fees}
                        onChange={(e) => setLandedCostForm({...landedCostForm, other_fees: parseFloat(e.target.value) || 0})}
                        className="bg-slate-700/50 border-slate-600 text-white mt-1"
                        placeholder="$0.00"
                      />
                    </div>
                  </div>
                  
                  <div className="p-4 rounded-xl bg-slate-700/30 border border-slate-600/50">
                    <Label className="text-slate-300">Sell Price (for margin)</Label>
                    <Input
                      type="number"
                      value={landedCostForm.sell_price}
                      onChange={(e) => setLandedCostForm({...landedCostForm, sell_price: parseFloat(e.target.value) || 0})}
                      className="bg-slate-700/50 border-slate-600 text-white mt-2"
                      placeholder="$0.00"
                    />
                  </div>
                  
                  <Button onClick={calculateLandedCost} className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white">
                    <Calculator className="w-4 h-4 mr-2" /> Calculate Landed Cost
                  </Button>
                </CardContent>
              </Card>
              
              {/* Results */}
              <Card className="bg-slate-800/50 border-slate-700/50">
                <CardHeader>
                  <CardTitle className="text-white">Results</CardTitle>
                </CardHeader>
                <CardContent>
                  {landedCostResult ? (
                    <div className="space-y-4">
                      <div className="p-5 rounded-xl bg-slate-700/30">
                        <p className="text-slate-400 text-sm">Total Import Cost</p>
                        <p className="text-4xl font-bold text-white mt-1">{formatCurrency(landedCostResult.total_cost)}</p>
                      </div>
                      
                      <div className="p-5 rounded-xl bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30">
                        <p className="text-cyan-400 text-sm font-medium">Unit Landed Cost</p>
                        <p className="text-4xl font-bold text-cyan-400 mt-1">{formatCurrency(landedCostResult.unit_landed_cost)}</p>
                        <p className="text-cyan-400/70 text-xs mt-1">per unit ({landedCostResult.total_units} units total)</p>
                      </div>
                      
                      {landedCostResult.sell_price > 0 && (
                        <>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="p-4 rounded-xl bg-slate-700/30">
                              <p className="text-slate-400 text-sm">Profit/Unit</p>
                              <p className={`text-2xl font-bold mt-1 ${landedCostResult.profit_per_unit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                {formatCurrency(landedCostResult.profit_per_unit)}
                              </p>
                            </div>
                            <div className="p-4 rounded-xl bg-slate-700/30">
                              <p className="text-slate-400 text-sm">Margin</p>
                              <p className={`text-2xl font-bold mt-1 ${landedCostResult.margin_percent >= 20 ? 'text-emerald-400' : 'text-amber-400'}`}>
                                {landedCostResult.margin_percent?.toFixed(1)}%
                              </p>
                            </div>
                          </div>
                          
                          <div className="p-5 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
                            <p className="text-emerald-400 text-sm">Total Potential Profit</p>
                            <p className="text-3xl font-bold text-emerald-400 mt-1">{formatCurrency(landedCostResult.total_potential_profit)}</p>
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Calculator className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                      <p className="text-slate-400">Enter your costs and click calculate</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Customers Tab */}
        {activeTab === "customers" && (
          <div className="space-y-6" data-testid="customers-content">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <h2 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Customers</h2>
                <p className={isDark ? 'text-slate-400 mt-1' : 'text-gray-500 mt-1'}>Manage your customer database</p>
              </div>
              <div className="flex gap-2 items-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadTemplate('customers')}
                  className={isDark ? 'border-slate-600 text-slate-200 hover:bg-slate-700' : ''}
                  data-testid="customers-download-template-btn"
                  title="Download a CSV template with the correct column names"
                >
                  <Download className="w-4 h-4 mr-2" /> Template
                </Button>
                <label className={`cursor-pointer ${customerUploadingState.active ? 'pointer-events-none opacity-50' : ''}`} title="Upload customers from CSV, Excel, Word, PDF, or images">
                  <input
                    type="file"
                    accept=".csv,.pdf,.docx,.xlsx,.xls,.jpg,.jpeg,.png,.webp"
                    multiple
                    className="hidden"
                    disabled={customerUploadingState.active}
                    data-testid="customers-file-upload"
                    onChange={async (e) => {
                      const files = Array.from(e.target.files || []);
                      if (files.length === 0) return;
                      let totalAdded = 0, totalSkipped = 0, failed = 0;
                      setCustomerUploadingState({ active: true, current: 0, total: files.length, filename: '' });
                      for (let i = 0; i < files.length; i++) {
                        const f = files[i];
                        const fd = new FormData();
                        fd.append('file', f);
                        setCustomerUploadingState({ active: true, current: i + 1, total: files.length, filename: f.name });
                        toast.loading(`Processing ${i + 1}/${files.length}: ${f.name}`, { id: 'cust-upload' });
                        try {
                          const r = await axios.post(`${API}/b/${businessSlug}/wholesale/customers/smart-upload`, fd, {
                            headers: { 'Content-Type': 'multipart/form-data' },
                            timeout: 180000,
                          });
                          totalAdded += r.data.added || 0;
                          totalSkipped += r.data.skipped || 0;
                          toast.success(`✓ ${f.name}: ${r.data.added || 0} customers added`, { duration: 3000 });
                        } catch (err) {
                          failed++;
                          toast.error(`Failed: ${f.name} — ${err.response?.data?.detail || 'Unknown error'}`, { duration: 6000 });
                        }
                      }
                      toast.dismiss('cust-upload');
                      setCustomerUploadingState({ active: false, current: 0, total: 0, filename: '' });
                      if (totalAdded > 0 || totalSkipped > 0) {
                        toast.success(`Upload complete! ${totalAdded} added${totalSkipped ? `, ${totalSkipped} skipped (duplicates)` : ''}${failed ? `, ${failed} failed` : ''}`, { duration: 5000 });
                        fetchAllData();
                      } else if (failed > 0) {
                        toast.error(`All ${failed} file(s) failed.`);
                      }
                      e.target.value = '';
                    }}
                  />
                  <span className={`inline-flex items-center px-4 py-2 rounded-md text-sm transition-colors ${isDark ? 'bg-slate-700/50 hover:bg-slate-700 border border-slate-600 text-white' : 'bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-700'}`}>
                    {customerUploadingState.active ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />{customerUploadingState.total > 1 ? `${customerUploadingState.current}/${customerUploadingState.total}` : 'Processing...'}</>
                    ) : (
                      <><Upload className="w-4 h-4 mr-2" /> Upload File</>
                    )}
                  </span>
                </label>
                <Button onClick={() => { resetCustomerForm(); setEditingCustomer(null); setShowCustomerModal(true); }} className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white">
                  <Plus className="w-4 h-4 mr-2" /> Add Customer
                </Button>
              </div>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Card className={isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-gray-200 shadow-sm'}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-cyan-500/20">
                      <Users className="w-5 h-5 text-cyan-500" />
                    </div>
                    <div>
                      <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{customers.length}</p>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Total Customers</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Customer List */}
            <Card className={`overflow-hidden ${isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-gray-200 shadow-sm'}`}>
              <CardContent className="p-0">
                {customers.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className={`border-b ${isDark ? 'bg-slate-700/30 border-slate-700/50' : 'bg-gray-50 border-gray-200'}`}>
                          <th className={`text-left p-4 text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Name</th>
                          <th className={`text-left p-4 text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Company</th>
                          <th className={`text-left p-4 text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Contact</th>
                          <th className={`text-left p-4 text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Notes</th>
                          <th className={`text-center p-4 text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Actions</th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${isDark ? 'divide-slate-700/30' : 'divide-gray-100'}`}>
                        {customers.map((customer) => (
                          <tr key={customer.id} className={`transition-colors ${isDark ? 'hover:bg-slate-700/20' : 'hover:bg-gray-50'}`}>
                            <td className="p-4">
                              <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{customer.name}</p>
                            </td>
                            <td className={`p-4 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>{customer.company || '-'}</td>
                            <td className="p-4">
                              <div className="space-y-1">
                                {customer.email && <p className={`text-sm flex items-center gap-1 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}><Mail className="w-3 h-3" /> {customer.email}</p>}
                                {customer.phone && <p className={`text-sm flex items-center gap-1 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}><Phone className="w-3 h-3" /> {customer.phone}</p>}
                              </div>
                            </td>
                            <td className={`p-4 max-w-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                              <p className="truncate text-sm">{customer.notes || '-'}</p>
                            </td>
                            <td className="p-4 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Button size="sm" variant="ghost" onClick={() => handleEditCustomer(customer)} className="h-8 w-8 p-0 hover:bg-cyan-500/20 text-cyan-500">
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setShowDeleteCustomerConfirm(customer)} className="h-8 w-8 p-0 hover:bg-red-500/20 text-red-500">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-12 text-center">
                    <Users className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-slate-600' : 'text-gray-400'}`} />
                    <p className={isDark ? 'text-slate-400 text-lg' : 'text-gray-500 text-lg'}>No customers yet</p>
                    <Button onClick={() => { resetCustomerForm(); setShowCustomerModal(true); }} className="mt-6 bg-gradient-to-r from-cyan-500 to-blue-600">
                      <Plus className="w-4 h-4 mr-2" /> Add First Customer
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === "payments" && (
          <div className="space-y-6" data-testid="payments-content">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <h2 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Payments Tracker</h2>
                <p className={isDark ? 'text-slate-400 mt-1' : 'text-gray-500 mt-1'}>Track all purchases, fees, and expenses</p>
              </div>
              <Button onClick={() => { resetPaymentForm(); setEditingPayment(null); setShowPaymentModal(true); }} className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white">
                <Plus className="w-4 h-4 mr-2" /> Record Payment
              </Button>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              <Card className={isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-gray-200 shadow-sm'}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-red-500/20">
                      <CreditCard className="w-5 h-5 text-red-500" />
                    </div>
                    <div>
                      <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(paymentsData.total_amount)}</p>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Total Spent</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className={isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-gray-200 shadow-sm'}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-500/20">
                      <Receipt className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                      <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(paymentsData.category_totals.order_payment || 0)}</p>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Order Payments</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className={isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-gray-200 shadow-sm'}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-amber-500/20">
                      <AlertCircle className="w-5 h-5 text-amber-500" />
                    </div>
                    <div>
                      <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency((paymentsData.category_totals.border_fee || 0) + (paymentsData.category_totals.customs || 0))}</p>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Border/Customs Fees</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className={isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-gray-200 shadow-sm'}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-purple-500/20">
                      <Ship className="w-5 h-5 text-purple-500" />
                    </div>
                    <div>
                      <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency((paymentsData.category_totals.shipping || 0) + (paymentsData.category_totals.drayage || 0))}</p>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Shipping/Trucking</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Category Filter */}
            <div className="flex gap-2 flex-wrap">
              <Button
                variant={selectedPaymentCategory === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedPaymentCategory("all")}
                className={selectedPaymentCategory === "all" ? "bg-cyan-500 hover:bg-cyan-600" : isDark ? "border-slate-600 text-slate-300" : ""}
              >
                All ({paymentsData.count})
              </Button>
              {PAYMENT_CATEGORIES.map(cat => (
                <Button
                  key={cat.value}
                  variant={selectedPaymentCategory === cat.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedPaymentCategory(cat.value)}
                  className={selectedPaymentCategory === cat.value ? "bg-cyan-500 hover:bg-cyan-600" : isDark ? "border-slate-600 text-slate-300" : ""}
                >
                  {cat.label} ({payments.filter(p => p.category === cat.value).length})
                </Button>
              ))}
            </div>
            
            {/* Payments List */}
            <Card className={`overflow-hidden ${isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-gray-200 shadow-sm'}`}>
              <CardContent className="p-0">
                {filteredPayments.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className={`border-b ${isDark ? 'bg-slate-700/30 border-slate-700/50' : 'bg-gray-50 border-gray-200'}`}>
                          <th className={`text-left p-4 text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Date</th>
                          <th className={`text-left p-4 text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Category</th>
                          <th className={`text-left p-4 text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Description</th>
                          <th className={`text-left p-4 text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Recipient</th>
                          <th className={`text-right p-4 text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Amount</th>
                          <th className={`text-center p-4 text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Actions</th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${isDark ? 'divide-slate-700/30' : 'divide-gray-100'}`}>
                        {filteredPayments.map((payment) => {
                          const catInfo = PAYMENT_CATEGORIES.find(c => c.value === payment.category) || { label: payment.category, color: 'text-slate-500' };
                          // Format date without timezone conversion
                          const formatPaymentDate = (dateStr) => {
                            if (!dateStr) return '-';
                            const [year, month, day] = dateStr.split('-');
                            return `${month}/${day}/${year}`;
                          };
                          return (
                            <tr key={payment.id} className={`transition-colors ${isDark ? 'hover:bg-slate-700/20' : 'hover:bg-gray-50'}`}>
                              <td className={`p-4 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                                {formatPaymentDate(payment.date)}
                              </td>
                              <td className="p-4">
                                <Badge className={`${catInfo.color} bg-opacity-20 border-0`} style={{backgroundColor: 'currentColor', opacity: 0.15}}>
                                  <span className={catInfo.color}>{catInfo.label}</span>
                                </Badge>
                              </td>
                              <td className="p-4">
                                <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{payment.description || '-'}</p>
                                {payment.reference && <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Ref: {payment.reference}</p>}
                              </td>
                              <td className={`p-4 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>{payment.recipient || '-'}</td>
                              <td className={`p-4 text-right font-bold text-red-500`}>{formatCurrency(payment.amount)}</td>
                              <td className="p-4 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <Button size="sm" variant="ghost" onClick={() => handleEditPayment(payment)} className="h-8 w-8 p-0 hover:bg-cyan-500/20 text-cyan-500">
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => setShowDeletePaymentConfirm(payment)} className="h-8 w-8 p-0 hover:bg-red-500/20 text-red-500">
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-12 text-center">
                    <CreditCard className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-slate-600' : 'text-gray-400'}`} />
                    <p className={isDark ? 'text-slate-400 text-lg' : 'text-gray-500 text-lg'}>No payments recorded yet</p>
                    <Button onClick={() => { resetPaymentForm(); setShowPaymentModal(true); }} className="mt-6 bg-gradient-to-r from-cyan-500 to-blue-600">
                      <Plus className="w-4 h-4 mr-2" /> Record First Payment
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === "settings" && (
          <SettingsTab 
            businessSlug={businessSlug}
            businessInfo={businessInfo}
            warehouses={warehouses}
            user={user}
            fetchAllData={fetchAllData}
          />
        )}
      </main>

      {/* Create Shipment Modal */}
      <Dialog open={showShipmentModal} onOpenChange={setShowShipmentModal}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ship className="w-5 h-5 text-cyan-400" /> New Shipment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            {/* Shipment Date */}
            <div className="flex items-center gap-4 p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
              <Calendar className="w-5 h-5 text-cyan-400" />
              <div>
                <Label className="text-cyan-400 text-xs">Shipment Date</Label>
                <Input 
                  type="date" 
                  value={shipmentForm.shipment_date} 
                  onChange={(e) => setShipmentForm({...shipmentForm, shipment_date: e.target.value})} 
                  className="bg-slate-700 border-slate-600 mt-1 w-44"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Container Number</Label>
                <Input value={shipmentForm.container_number} onChange={(e) => setShipmentForm({...shipmentForm, container_number: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" placeholder="CSNU1234567" />
              </div>
              <div>
                <Label>Bill of Lading</Label>
                <Input value={shipmentForm.bill_of_lading} onChange={(e) => setShipmentForm({...shipmentForm, bill_of_lading: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" />
              </div>
            </div>
            <div>
              <Label>Customs Entry # (7501)</Label>
              <Input value={shipmentForm.customs_entry_number} onChange={(e) => setShipmentForm({...shipmentForm, customs_entry_number: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Origin Country</Label>
                <Input value={shipmentForm.origin_country} onChange={(e) => setShipmentForm({...shipmentForm, origin_country: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" placeholder="China" />
              </div>
              <div>
                <Label>Origin Port</Label>
                <Input value={shipmentForm.origin_port} onChange={(e) => setShipmentForm({...shipmentForm, origin_port: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" placeholder="Shanghai" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Destination Port</Label>
                <Input value={shipmentForm.destination_port} onChange={(e) => setShipmentForm({...shipmentForm, destination_port: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" placeholder="Los Angeles" />
              </div>
              <div>
                <Label>Final Destination</Label>
                <Input value={shipmentForm.final_destination} onChange={(e) => setShipmentForm({...shipmentForm, final_destination: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" placeholder="MD Warehouse" />
              </div>
            </div>
            <div>
              <Label>Estimated Arrival</Label>
              <Input type="date" value={shipmentForm.estimated_arrival} onChange={(e) => setShipmentForm({...shipmentForm, estimated_arrival: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Total Cartons</Label>
                <Input type="number" value={shipmentForm.total_cartons} onChange={(e) => setShipmentForm({...shipmentForm, total_cartons: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" placeholder="0" />
              </div>
              <div>
                <Label>Total Units</Label>
                <Input type="number" value={shipmentForm.total_units} onChange={(e) => setShipmentForm({...shipmentForm, total_units: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" placeholder="0" />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={shipmentForm.notes} onChange={(e) => setShipmentForm({...shipmentForm, notes: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShipmentModal(false)} className="border-slate-600 text-slate-300">Cancel</Button>
            <Button onClick={handleCreateShipment} className="bg-gradient-to-r from-cyan-500 to-blue-600">Create Shipment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shipment Detail/Edit Modal */}
      <Dialog open={showShipmentDetailModal} onOpenChange={(open) => {
        if (!open) {
          setShowShipmentDetailModal(false);
          setEditingShipment(null);
          resetShipmentForm();
        }
      }}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ship className="w-5 h-5 text-cyan-400" /> Edit Shipment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Container #</Label>
                <Input value={shipmentForm.container_number} onChange={(e) => setShipmentForm({...shipmentForm, container_number: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" />
              </div>
              <div>
                <Label>Bill of Lading</Label>
                <Input value={shipmentForm.bill_of_lading} onChange={(e) => setShipmentForm({...shipmentForm, bill_of_lading: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" />
              </div>
            </div>
            <div>
              <Label>Customs Entry #</Label>
              <Input value={shipmentForm.customs_entry_number} onChange={(e) => setShipmentForm({...shipmentForm, customs_entry_number: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Origin Country</Label>
                <Input value={shipmentForm.origin_country} onChange={(e) => setShipmentForm({...shipmentForm, origin_country: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" />
              </div>
              <div>
                <Label>Origin Port</Label>
                <Input value={shipmentForm.origin_port} onChange={(e) => setShipmentForm({...shipmentForm, origin_port: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Destination Port</Label>
                <Input value={shipmentForm.destination_port} onChange={(e) => setShipmentForm({...shipmentForm, destination_port: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" />
              </div>
              <div>
                <Label>Final Destination</Label>
                <Input value={shipmentForm.final_destination} onChange={(e) => setShipmentForm({...shipmentForm, final_destination: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" />
              </div>
            </div>
            <div>
              <Label>Status</Label>
              <select 
                value={shipmentForm.status} 
                onChange={(e) => setShipmentForm({...shipmentForm, status: e.target.value})}
                className="w-full bg-slate-700 border border-slate-600 rounded-md mt-1 p-2 text-white text-sm"
              >
                <option value="booked">Booked</option>
                <option value="on_water">On Water</option>
                <option value="at_port">At Port</option>
                <option value="customs">In Customs</option>
                <option value="released">Released</option>
                <option value="delivered">Delivered</option>
              </select>
            </div>
            <div>
              <Label>Estimated Arrival</Label>
              <Input type="date" value={shipmentForm.estimated_arrival} onChange={(e) => setShipmentForm({...shipmentForm, estimated_arrival: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Total Cartons</Label>
                <Input type="number" value={shipmentForm.total_cartons} onChange={(e) => setShipmentForm({...shipmentForm, total_cartons: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" />
              </div>
              <div>
                <Label>Total Units</Label>
                <Input type="number" value={shipmentForm.total_units} onChange={(e) => setShipmentForm({...shipmentForm, total_units: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={shipmentForm.notes} onChange={(e) => setShipmentForm({...shipmentForm, notes: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" rows={2} />
            </div>
          </div>
          <DialogFooter className="flex justify-between items-center">
            <div className="flex gap-2">
              <Link to={`/${businessSlug}/wholesale/shipments/${editingShipment?.id}/print`} target="_blank">
                <Button variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700" data-testid="shipment-modal-view-document-btn">
                  <FileText className="w-4 h-4 mr-2" /> View Document
                </Button>
              </Link>
              <Link to={`/${businessSlug}/wholesale/shipments/${editingShipment?.id}/print`} target="_blank">
                <Button variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700">
                  <Printer className="w-4 h-4 mr-2" /> Print
                </Button>
              </Link>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setShowShipmentDetailModal(false); setEditingShipment(null); resetShipmentForm(); }} className="border-slate-600 text-slate-300">Cancel</Button>
              <Button onClick={handleUpdateShipment} className="bg-gradient-to-r from-cyan-500 to-blue-600">Save Changes</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Product Modal */}
      <Dialog open={showProductModal} onOpenChange={(open) => {
        if (!open) {
          setShowProductModal(false);
          setEditingProduct(null);
          resetProductForm();
        } else {
          setShowProductModal(open);
        }
      }}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-cyan-400" /> {editingProduct ? 'Edit Product' : 'Add Product'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>SKU *</Label>
                <Input value={productForm.sku} onChange={(e) => setProductForm({...productForm, sku: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" placeholder="SF-001" />
              </div>
              <div>
                <Label>HS Code</Label>
                <Input value={productForm.hs_code} onChange={(e) => setProductForm({...productForm, hs_code: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" placeholder="1206.00" />
              </div>
            </div>
            <div>
              <Label>Product Name *</Label>
              <Input value={productForm.name} onChange={(e) => setProductForm({...productForm, name: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" placeholder="Roasted Sunflower Seeds 200g" />
            </div>
            <div>
              <Label>Description</Label>
              <textarea 
                value={productForm.description} 
                onChange={(e) => setProductForm({...productForm, description: e.target.value})} 
                className="w-full bg-slate-700 border border-slate-600 rounded-md mt-1 p-2 text-white text-sm min-h-[80px] resize-none focus:outline-none focus:ring-1 focus:ring-cyan-500" 
                placeholder="Enter product details, specifications, packaging info, etc."
              />
            </div>
            <div>
              <Label>Category</Label>
              <Input value={productForm.category} onChange={(e) => setProductForm({...productForm, category: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" placeholder="Seeds, Snacks, Beverages..." />
            </div>
            <div>
              <Label>Product Image</Label>
              <div className="flex gap-2 mt-1">
                <label className="flex-1 cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        if (file.size > 2 * 1024 * 1024) {
                          toast.error("Image must be less than 2MB");
                          return;
                        }
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setProductForm({...productForm, image_url: reader.result});
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                  <div className="flex items-center justify-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 border border-slate-600 rounded-md transition-colors">
                    <Upload className="w-4 h-4" />
                    <span className="text-sm">Upload Image</span>
                  </div>
                </label>
              </div>
              {productForm.image_url && (
                <div className="mt-3 flex items-center gap-3">
                  <img src={productForm.image_url} alt="Preview" className="w-16 h-16 object-cover rounded border border-slate-600" onError={(e) => e.target.style.display='none'} />
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setProductForm({...productForm, image_url: ''})}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> Remove
                  </Button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Purchase Price ($)</Label>
                <Input 
                  type="number" 
                  step="0.01" 
                  value={productForm.purchase_price} 
                  onChange={(e) => setProductForm({...productForm, purchase_price: e.target.value})} 
                  className="bg-slate-700 border-slate-600 mt-1" 
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Sell Price ($)</Label>
                <Input 
                  type="number" 
                  step="0.01" 
                  value={productForm.sell_price} 
                  onChange={(e) => setProductForm({...productForm, sell_price: e.target.value})} 
                  className="bg-slate-700 border-slate-600 mt-1" 
                  placeholder="0.00"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Stock Qty</Label>
                <Input 
                  type="number" 
                  value={productForm.stock_quantity} 
                  onChange={(e) => setProductForm({...productForm, stock_quantity: e.target.value})} 
                  className="bg-slate-700 border-slate-600 mt-1" 
                  placeholder="0"
                />
              </div>
              <div>
                <Label>Units per Package</Label>
                <Input 
                  type="number" 
                  value={productForm.units_per_carton} 
                  onChange={(e) => setProductForm({...productForm, units_per_carton: e.target.value})} 
                  className="bg-slate-700 border-slate-600 mt-1" 
                  placeholder="1"
                />
              </div>
              <div>
                <Label>Package Type</Label>
                <select 
                  value={productForm.carton_type} 
                  onChange={(e) => setProductForm({...productForm, carton_type: e.target.value})}
                  className="w-full bg-slate-700 border border-slate-600 rounded-md mt-1 p-2 text-white text-sm"
                >
                  <option value="carton">Carton</option>
                  <option value="pallet">Pallet</option>
                  <option value="bag">Bag</option>
                  <option value="box">Box</option>
                  <option value="case">Case</option>
                  <option value="unit">Unit</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Warehouse</Label>
                <Input value={productForm.warehouse_location} onChange={(e) => setProductForm({...productForm, warehouse_location: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" placeholder="MD" />
              </div>
              <div>
                <Label>Country of Origin</Label>
                <Input value={productForm.country_of_origin} onChange={(e) => setProductForm({...productForm, country_of_origin: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" placeholder="China" />
              </div>
            </div>
            <div>
              <Label>Supplier</Label>
              <Input value={productForm.supplier} onChange={(e) => setProductForm({...productForm, supplier: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowProductModal(false); setEditingProduct(null); resetProductForm(); }} className="border-slate-600 text-slate-300">Cancel</Button>
            <Button onClick={editingProduct ? handleUpdateProduct : handleCreateProduct} className="bg-gradient-to-r from-cyan-500 to-blue-600">
              {editingProduct ? 'Save Changes' : 'Add Product'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Invoice Modal - Full Featured */}
      {/* Invoice Creation/Edit Modal - Prevents accidental close */}
      <Dialog open={showInvoiceModal} onOpenChange={(open) => {
        if (!open) {
          // Ask for confirmation before closing if form has data
          const hasData = invoiceForm.consignee_name || invoiceForm.items.some(i => i.description);
          if (hasData) {
            if (confirm('You have unsaved changes. Are you sure you want to close?')) {
              setShowInvoiceModal(false);
              setEditingInvoiceId(null);
              resetInvoiceForm();
            }
          } else {
            setShowInvoiceModal(false);
            setEditingInvoiceId(null);
          }
        } else {
          setShowInvoiceModal(open);
        }
      }}>
        <DialogContent 
          className="max-w-4xl bg-slate-800 border-slate-700 text-white max-h-[90vh] overflow-y-auto"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <FileText className="w-6 h-6 text-cyan-400" /> {editingInvoiceId ? 'Edit Invoice' : 'Create Wholesale Invoice'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Invoice Date */}
            <div className="flex items-center gap-4 p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
              <Calendar className="w-5 h-5 text-cyan-400" />
              <div>
                <Label className="text-cyan-400 text-xs">Invoice Date</Label>
                <Input 
                  type="date" 
                  value={invoiceForm.invoice_date} 
                  onChange={(e) => setInvoiceForm({...invoiceForm, invoice_date: e.target.value})} 
                  className="bg-slate-700 border-slate-600 mt-1 w-44"
                />
              </div>
            </div>
            
            {/* Consignee Info */}
            <div className="p-4 rounded-xl bg-slate-700/30 border border-slate-600/50">
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                <User className="w-4 h-4 text-cyan-400" /> Consignee (Bill To)
              </h3>
              
              {/* Customer Selector */}
              {customers.length > 0 && (
                <div className="mb-4">
                  <Label>Select Existing Customer</Label>
                  <Select onValueChange={(v) => {
                    if (v) {
                      const customer = customers.find(c => c.id === v);
                      if (customer) {
                        setInvoiceForm({
                          ...invoiceForm,
                          consignee_name: customer.name || '',
                          consignee_company: customer.company || '',
                          consignee_email: customer.email || '',
                          consignee_phone: customer.phone || '',
                          consignee_address: [customer.address, customer.city, customer.state, customer.zip_code].filter(Boolean).join(', ')
                        });
                      }
                    }
                  }}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 mt-1">
                      <SelectValue placeholder="Choose a saved customer or enter new below" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">
                      {customers.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name} {c.company ? `(${c.company})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Company Name</Label>
                  <Input value={invoiceForm.consignee_company} onChange={(e) => setInvoiceForm({...invoiceForm, consignee_company: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" placeholder="ABC Distribution Inc." />
                </div>
                <div>
                  <Label>Contact Name *</Label>
                  <Input value={invoiceForm.consignee_name} onChange={(e) => setInvoiceForm({...invoiceForm, consignee_name: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" placeholder="John Smith" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={invoiceForm.consignee_email} onChange={(e) => setInvoiceForm({...invoiceForm, consignee_email: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={invoiceForm.consignee_phone} onChange={(e) => setInvoiceForm({...invoiceForm, consignee_phone: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" />
                </div>
                <div className="col-span-2">
                  <Label>Address</Label>
                  <Input value={invoiceForm.consignee_address} onChange={(e) => setInvoiceForm({...invoiceForm, consignee_address: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" placeholder="123 Main St, City, State ZIP" />
                </div>
              </div>
            </div>
            
            {/* Shipping Info */}
            <div className="p-4 rounded-xl bg-slate-700/30 border border-slate-600/50">
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                <Truck className="w-4 h-4 text-cyan-400" /> Shipping Details
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Ship From (Warehouse)</Label>
                  <Input value={invoiceForm.ship_from_warehouse} onChange={(e) => setInvoiceForm({...invoiceForm, ship_from_warehouse: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" placeholder="MD Warehouse" />
                </div>
                <div>
                  <Label>Final Destination</Label>
                  <Input value={invoiceForm.final_destination} onChange={(e) => setInvoiceForm({...invoiceForm, final_destination: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" />
                </div>
                <div>
                  <Label>BOL Reference</Label>
                  <Input value={invoiceForm.bill_of_lading_ref} onChange={(e) => setInvoiceForm({...invoiceForm, bill_of_lading_ref: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" />
                </div>
              </div>
            </div>
            
            {/* Line Items */}
            <div className="p-4 rounded-xl bg-slate-700/30 border border-slate-600/50">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <ClipboardList className="w-4 h-4 text-cyan-400" /> Line Items
                </h3>
                <Button size="sm" variant="outline" onClick={addInvoiceItem} className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10">
                  <Plus className="w-4 h-4 mr-1" /> Add Item
                </Button>
              </div>
              
              <div className="space-y-2">
                {invoiceForm.items.map((item, index) => (
                  <div key={index} className="p-3 rounded-lg bg-slate-800/50 border border-slate-600/30">
                    {/* Row 1: Item Number with Move Buttons, Description, SKU, Delete */}
                    <div className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-1 flex items-center gap-1">
                        <div className="flex flex-col">
                          <button 
                            onClick={() => moveInvoiceItemUp(index)} 
                            disabled={index === 0}
                            className={`p-0.5 rounded ${index === 0 ? 'text-slate-600 cursor-not-allowed' : 'text-slate-400 hover:text-cyan-400 hover:bg-slate-700'}`}
                          >
                            <ChevronUp className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => moveInvoiceItemDown(index)} 
                            disabled={index === invoiceForm.items.length - 1}
                            className={`p-0.5 rounded ${index === invoiceForm.items.length - 1 ? 'text-slate-600 cursor-not-allowed' : 'text-slate-400 hover:text-cyan-400 hover:bg-slate-700'}`}
                          >
                            <ChevronDown className="w-4 h-4" />
                          </button>
                        </div>
                        <span className="text-cyan-400 font-bold text-lg">{index + 1}</span>
                      </div>
                      <div className="col-span-10">
                        <Label className="text-xs text-slate-400">Search Product</Label>
                        <div className="relative">
                          <Input 
                            value={item.description} 
                            onChange={(e) => {
                              updateInvoiceItem(index, 'description', e.target.value);
                              updateInvoiceItem(index, 'showSuggestions', true);
                            }}
                            onFocus={() => updateInvoiceItem(index, 'showSuggestions', true)}
                            onBlur={() => setTimeout(() => updateInvoiceItem(index, 'showSuggestions', false), 200)}
                            className="bg-slate-700 border-slate-600 mt-1 text-sm" 
                            placeholder="Start typing product name..." 
                            autoComplete="off"
                          />
                          {/* Autocomplete Dropdown */}
                          {item.showSuggestions && item.description && (
                            <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-700 rounded-md shadow-lg max-h-60 overflow-y-auto">
                              {products
                                .filter(p => 
                                  p.name.toLowerCase().includes(item.description.toLowerCase()) ||
                                  p.sku.toLowerCase().includes(item.description.toLowerCase()) ||
                                  (p.category && p.category.toLowerCase().includes(item.description.toLowerCase()))
                                )
                                .slice(0, 10)
                                .map(product => (
                                  <div
                                    key={product.id}
                                    className="flex items-center gap-3 p-2 hover:bg-slate-700 cursor-pointer border-b border-slate-700/50 last:border-0"
                                    onMouseDown={() => {
                                      updateInvoiceItem(index, 'description', product.name);
                                      updateInvoiceItem(index, 'sku', product.sku);
                                      updateInvoiceItem(index, 'product_id', product.id);
                                      updateInvoiceItem(index, 'image_url', product.image_url || '');
                                      updateInvoiceItem(index, 'unit_price', product.sell_price || 0);
                                      updateInvoiceItem(index, 'showSuggestions', false);
                                    }}
                                  >
                                    {product.image_url ? (
                                      <img src={product.image_url} alt="" className="w-10 h-10 rounded object-cover border border-slate-600 flex-shrink-0" />
                                    ) : (
                                      <div className="w-10 h-10 rounded bg-slate-700 flex items-center justify-center border border-slate-600 flex-shrink-0">
                                        <Package className="w-5 h-5 text-slate-500" />
                                      </div>
                                    )}
                                    <div className="flex flex-col flex-1 min-w-0">
                                      <span className="text-sm font-medium text-slate-200 truncate">{product.name}</span>
                                      <span className="text-xs text-slate-400">{product.sku} • ${product.sell_price} • {product.stock_quantity} in stock</span>
                                    </div>
                                  </div>
                                ))
                              }
                              {products.filter(p => 
                                p.name.toLowerCase().includes(item.description.toLowerCase()) ||
                                p.sku.toLowerCase().includes(item.description.toLowerCase())
                              ).length === 0 && (
                                <div className="p-3 text-center text-slate-500 text-sm">
                                  No products found matching "{item.description}"
                                </div>
                              )}
                            </div>
                          )}
                          {/* Show selected product with image */}
                          {item.sku && item.image_url && (
                            <div className="absolute right-2 top-1/2 -translate-y-1/2 mt-0.5">
                              <img src={item.image_url} alt="" className="w-7 h-7 rounded object-cover border border-cyan-500" />
                            </div>
                          )}
                        </div>
                        {item.sku && (
                          <div className="text-xs text-cyan-400 mt-1">Selected: {item.sku}</div>
                        )}
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <Button size="sm" variant="ghost" onClick={() => removeInvoiceItem(index)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-9 w-9 p-0" disabled={invoiceForm.items.length === 1}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    {/* Row 2: Units, Cases, Qty/Case, Unit Price, Total */}
                    <div className="grid grid-cols-12 gap-2 items-end mt-2 pt-2 border-t border-slate-700/50">
                      <div className="col-span-2">
                        <Label className="text-xs text-slate-400">Cases</Label>
                        <Input 
                          type="number" 
                          min="1"
                          value={item.cases || ''} 
                          onChange={(e) => {
                            const cases = e.target.value === '' ? '' : parseInt(e.target.value) || 1;
                            updateInvoiceItem(index, 'cases', cases);
                          }}
                          onBlur={(e) => { if (!e.target.value) updateInvoiceItem(index, 'cases', 1); }}
                          className="bg-slate-700 border-slate-600 mt-1 text-sm"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs text-slate-400">Qty/Case</Label>
                        <Input 
                          type="number" 
                          min="1"
                          value={item.qty_per_case || ''} 
                          onChange={(e) => {
                            const qty_per_case = e.target.value === '' ? '' : parseInt(e.target.value) || 1;
                            updateInvoiceItem(index, 'qty_per_case', qty_per_case);
                          }}
                          onBlur={(e) => { if (!e.target.value) updateInvoiceItem(index, 'qty_per_case', 1); }}
                          className="bg-slate-700 border-slate-600 mt-1 text-sm"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs text-slate-400">Total Units</Label>
                        <Input 
                          type="number" 
                          min="1"
                          value={item.quantity || ''} 
                          onChange={(e) => {
                            const quantity = e.target.value === '' ? '' : parseInt(e.target.value) || 1;
                            updateInvoiceItem(index, 'quantity', quantity);
                          }}
                          onBlur={(e) => { if (!e.target.value) updateInvoiceItem(index, 'quantity', 1); }}
                          className="bg-slate-700 border-slate-600 mt-1 text-sm"
                          title="Auto-calculated from Cases × Qty/Case. You can edit manually if needed."
                        />
                      </div>
                      <div className="col-span-3">
                        <Label className="text-xs text-slate-400">Unit Price</Label>
                        <Input 
                          type="number" 
                          step="0.01" 
                          min="0"
                          value={item.unit_price || ''} 
                          onChange={(e) => updateInvoiceItem(index, 'unit_price', e.target.value === '' ? '' : parseFloat(e.target.value) || 0)} 
                          onBlur={(e) => { if (!e.target.value) updateInvoiceItem(index, 'unit_price', 0); }}
                          className="bg-slate-700 border-slate-600 mt-1 text-sm" 
                          placeholder="$0.00"
                        />
                      </div>
                      <div className="col-span-3 text-right">
                        <Label className="text-xs text-slate-500">Total</Label>
                        <div className="text-cyan-400 font-semibold text-sm mt-2">
                          {formatCurrency((item.quantity || 1) * (item.unit_price || 0))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Simplified Totals Summary */}
              <div className="mt-4 p-3 rounded-lg bg-slate-800/80 border border-cyan-500/30">
                <div className="flex justify-between items-center">
                  <div className="flex gap-6">
                    <div>
                      <span className="text-slate-400 text-xs">Items: </span>
                      <span className="text-white font-semibold">{invoiceForm.items.length}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 text-xs">Total Cases: </span>
                      <span className="text-white font-semibold">{calculateInvoiceTotals().totalCases}</span>
                    </div>
                  </div>
                  <div>
                    <span className="text-slate-400 text-sm">Subtotal: </span>
                    <span className="text-xl font-bold text-cyan-400">{formatCurrency(calculateInvoiceTotals().subtotal)}</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Tax & Fee Options */}
            <div className="p-4 rounded-xl bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30">
              <h3 className="font-semibold text-amber-400 mb-4 flex items-center gap-2">
                <DollarSign className="w-4 h-4" /> Tax & Fee Options
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Tax Rate (%)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={invoiceForm.tax_rate}
                    onChange={(e) => setInvoiceForm({...invoiceForm, tax_rate: parseFloat(e.target.value) || 0})}
                    className="bg-slate-700 border-slate-600 mt-1"
                    placeholder="6.00"
                    disabled={invoiceForm.is_tax_exempt}
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Card Fee (%)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={invoiceForm.card_fee_percent}
                    onChange={(e) => setInvoiceForm({...invoiceForm, card_fee_percent: parseFloat(e.target.value) || 3})}
                    className="bg-slate-700 border-slate-600 mt-1"
                    placeholder="3.00"
                    disabled={!invoiceForm.card_fee_enabled}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-700/30">
                  <div>
                    <p className="font-medium text-white text-sm">Tax Exempt</p>
                    <p className="text-xs text-slate-400">No tax for this invoice</p>
                  </div>
                  <Switch
                    checked={invoiceForm.is_tax_exempt}
                    onCheckedChange={(checked) => setInvoiceForm({...invoiceForm, is_tax_exempt: checked})}
                  />
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-700/30">
                  <div>
                    <p className="font-medium text-white text-sm">Apply Card Fee</p>
                    <p className="text-xs text-slate-400">Add processing fee</p>
                  </div>
                  <Switch
                    checked={invoiceForm.card_fee_enabled}
                    onCheckedChange={(checked) => setInvoiceForm({...invoiceForm, card_fee_enabled: checked})}
                  />
                </div>
              </div>
              
              {/* Live Total Calculation */}
              <div className="mt-4 p-3 rounded-lg bg-slate-900/50 border border-slate-600/50">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Subtotal</span>
                    <span className="text-white">{formatCurrency(calculateInvoiceTotals().subtotal)}</span>
                  </div>
                  {!invoiceForm.is_tax_exempt && invoiceForm.tax_rate > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Tax ({invoiceForm.tax_rate}%)</span>
                      <span className="text-white">{formatCurrency(calculateInvoiceTotals().taxAmount)}</span>
                    </div>
                  )}
                  {invoiceForm.card_fee_enabled && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Card Fee ({invoiceForm.card_fee_percent}%)</span>
                      <span className="text-white">{formatCurrency(calculateInvoiceTotals().cardFeeAmount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold pt-2 border-t border-slate-600/50">
                    <span className="text-cyan-400">TOTAL</span>
                    <span className="text-cyan-400">{formatCurrency(calculateInvoiceTotals().total)}</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Payment & Notes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 rounded-xl bg-slate-700/30 border border-slate-600/50">
                <Label>Payment Terms</Label>
                <select value={invoiceForm.payment_terms} onChange={(e) => setInvoiceForm({...invoiceForm, payment_terms: e.target.value})} className="w-full bg-slate-700 border border-slate-600 rounded-md px-3 py-2 mt-1 text-white">
                  <option value="due_on_receipt">Due on Receipt</option>
                  <option value="net_30">Net 30</option>
                  <option value="net_60">Net 60</option>
                </select>
                {/* 50/50 Payment Toggle */}
                <label className="flex items-center gap-2 mt-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={invoiceForm.show_50_percent_terms}
                    onChange={(e) => setInvoiceForm({...invoiceForm, show_50_percent_terms: e.target.checked})}
                    className="w-4 h-4 rounded border-slate-500 bg-slate-700 text-cyan-500 focus:ring-cyan-500"
                  />
                  <span className="text-sm text-slate-300">50% upfront, 50% on delivery</span>
                </label>
              </div>
              <div className="p-4 rounded-xl bg-slate-700/30 border border-slate-600/50">
                <Label>Thank You Message</Label>
                <Input value={invoiceForm.custom_message} onChange={(e) => setInvoiceForm({...invoiceForm, custom_message: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" />
              </div>
            </div>
            
            <div>
              <Label>Notes / Special Instructions</Label>
              <Textarea value={invoiceForm.notes} onChange={(e) => setInvoiceForm({...invoiceForm, notes: e.target.value})} className="bg-slate-700 border-slate-600 mt-1" rows={3} placeholder="Add any special instructions, terms, or notes..." />
            </div>
          </div>
          
          <DialogFooter className="mt-6 gap-3">
            <Button variant="outline" onClick={() => setShowInvoiceModal(false)} className="border-slate-600 text-slate-300">Cancel</Button>
            <Button onClick={handleCreateInvoice} className="bg-gradient-to-r from-cyan-500 to-blue-600 px-8">
              <FileCheck className="w-4 h-4 mr-2" /> Create Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Detail Modal */}
      {/* Invoice Detail Modal - Enhanced with email, edit, delete, status */}
      <Dialog open={showInvoiceDetailModal} onOpenChange={setShowInvoiceDetailModal}>
        <DialogContent className="max-w-3xl bg-slate-800 border-slate-700 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-xl">Invoice {selectedInvoice?.invoice_number}</DialogTitle>
              {selectedInvoice?.email_sent && (
                <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">
                  Emailed {selectedInvoice.email_sent_to ? `to ${selectedInvoice.email_sent_to}` : ''}
                </span>
              )}
            </div>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4">
              {/* Header Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-slate-400 text-sm">Consignee</p>
                  <p className="font-semibold text-white">{selectedInvoice.consignee_company || selectedInvoice.consignee_name}</p>
                  <p className="text-sm text-slate-400">{selectedInvoice.consignee_address}</p>
                  {selectedInvoice.consignee_email && (
                    <p className="text-sm text-cyan-400">{selectedInvoice.consignee_email}</p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-slate-400 text-sm">Total</p>
                  <p className="text-3xl font-bold text-white">{formatCurrency(selectedInvoice.total)}</p>
                  <select 
                    value={selectedInvoice.payment_status}
                    onChange={async (e) => {
                      try {
                        await axios.put(`${API}/b/${businessSlug}/wholesale/invoices/${selectedInvoice.id}/status?status=${e.target.value}`);
                        toast.success(`Status updated to ${e.target.value}`);
                        fetchAllData();
                        setSelectedInvoice({ ...selectedInvoice, payment_status: e.target.value });
                      } catch (err) {
                        toast.error("Failed to update status");
                      }
                    }}
                    className={`mt-2 px-3 py-1 rounded-full text-sm font-medium cursor-pointer ${
                      selectedInvoice.payment_status === 'paid' ? 'bg-green-500/20 text-green-400' :
                      selectedInvoice.payment_status === 'partial' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-orange-500/20 text-orange-400'
                    }`}
                  >
                    <option value="pending">PENDING</option>
                    <option value="partial">PARTIAL</option>
                    <option value="paid">PAID</option>
                  </select>
                </div>
              </div>
              
              {/* Shipping & Weight Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-3 rounded-lg bg-slate-700/30">
                <div className="text-center">
                  <p className="text-xs text-slate-400">Weight</p>
                  <p className="font-bold text-white">{selectedInvoice.total_weight_lbs?.toLocaleString() || 0} lbs</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-400">Pallets</p>
                  <p className="font-bold text-white">{selectedInvoice.total_pallets || 0}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-400">Terms</p>
                  <p className="font-bold text-white">{selectedInvoice.payment_terms?.replace('_', ' ').toUpperCase()}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-400">Due</p>
                  <p className="font-bold text-white">{selectedInvoice.due_date ? new Date(selectedInvoice.due_date).toLocaleDateString() : 'On Receipt'}</p>
                </div>
              </div>
              
              {/* Items */}
              <div className="border-t border-slate-700 pt-4">
                <p className="text-slate-400 text-sm mb-2">Items ({selectedInvoice.items?.length || 0})</p>
                <div className="max-h-48 overflow-y-auto">
                  {selectedInvoice.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between py-2 border-b border-slate-700/50">
                      <div>
                        <p className="text-white">{item.name || item.description}</p>
                        <p className="text-xs text-slate-500">{item.sku} • {item.quantity_units || item.quantity} {item.unit || 'units'} • {item.weight_lbs || 0} lbs</p>
                      </div>
                      <p className="font-semibold text-white">{formatCurrency(item.total || ((item.quantity_units || item.quantity) * item.unit_price))}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Totals */}
              <div className="bg-slate-700/30 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Subtotal</span>
                  <span className="text-white">{formatCurrency(selectedInvoice.subtotal)}</span>
                </div>
                {selectedInvoice.tax_amount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Tax ({selectedInvoice.tax_rate}%)</span>
                    <span className="text-white">{formatCurrency(selectedInvoice.tax_amount)}</span>
                  </div>
                )}
                {selectedInvoice.card_fee_amount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Card Fee ({selectedInvoice.card_fee_percent}%)</span>
                    <span className="text-white">{formatCurrency(selectedInvoice.card_fee_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold pt-2 border-t border-slate-600">
                  <span className="text-cyan-400">TOTAL</span>
                  <span className="text-cyan-400">{formatCurrency(selectedInvoice.total)}</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Action Buttons */}
          <DialogFooter className="flex-col sm:flex-row gap-2 mt-4">
            <div className="flex gap-2 w-full sm:w-auto">
              <Button 
                variant="outline" 
                onClick={async () => {
                  if (confirm('Are you sure you want to delete this invoice?')) {
                    try {
                      await axios.delete(`${API}/b/${businessSlug}/wholesale/invoices/${selectedInvoice.id}`);
                      toast.success('Invoice deleted');
                      setShowInvoiceDetailModal(false);
                      fetchAllData();
                    } catch (err) {
                      toast.error('Failed to delete invoice');
                    }
                  }
                }} 
                className="border-red-500/50 text-red-400 hover:bg-red-500/20"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Delete
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  // Open edit modal (reuse create modal with pre-filled data)
                  setInvoiceForm({
                    invoice_date: selectedInvoice.invoice_date || selectedInvoice.created_at?.split('T')[0] || new Date().toISOString().split('T')[0],
                    consignee_name: selectedInvoice.consignee_name || '',
                    consignee_company: selectedInvoice.consignee_company || '',
                    consignee_email: selectedInvoice.consignee_email || '',
                    consignee_phone: selectedInvoice.consignee_phone || '',
                    consignee_address: selectedInvoice.consignee_address || '',
                    bill_of_lading_ref: selectedInvoice.bill_of_lading_ref || '',
                    ship_from_warehouse: selectedInvoice.ship_from_warehouse || '',
                    final_destination: selectedInvoice.final_destination || '',
                    payment_terms: selectedInvoice.payment_terms || 'due_on_receipt',
                    notes: selectedInvoice.notes || '',
                    custom_message: selectedInvoice.custom_message || 'Thank you for your business!',
                    tax_rate: selectedInvoice.tax_rate || 0,
                    is_tax_exempt: selectedInvoice.is_tax_exempt || false,
                    card_fee_enabled: selectedInvoice.card_fee_enabled || false,
                    card_fee_percent: selectedInvoice.card_fee_percent || 3,
                    items: selectedInvoice.items?.map(item => {
                      const qty = item.quantity_units || item.quantity || 1;
                      return {
                        description: item.name || item.description || '',
                        sku: item.sku || '',
                        product_id: item.product_id || '',
                        image_url: item.image_url || '',
                        cases: item.cases || qty,  // Use existing cases or fall back to quantity
                        qty_per_case: item.qty_per_case || 1,  // Use existing qty_per_case or default to 1
                        quantity: qty,
                        unit: item.unit || 'units',
                        pkg_type: item.pkg_type || item.package_type || 'boxes',
                        weight_lbs: item.weight_lbs || 0,
                        pallets: item.pallets || 0,
                        unit_price: item.unit_price || 0
                      };
                    }) || [{ description: '', sku: '', product_id: '', image_url: '', cases: 1, qty_per_case: 1, quantity: 1, unit: 'units', pkg_type: 'boxes', weight_lbs: 0, pallets: 0, unit_price: 0 }]
                  });
                  setEditingInvoiceId(selectedInvoice.id);
                  setShowInvoiceDetailModal(false);
                  setShowInvoiceModal(true);
                }} 
                className="border-slate-600"
              >
                <Edit className="w-4 h-4 mr-1" /> Edit
              </Button>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button 
                variant="outline"
                onClick={async () => {
                  // Always ask for email - pre-fill with consignee email but allow user to change
                  const defaultEmail = selectedInvoice.consignee_email || '';
                  const email = prompt('Send invoice to email address:', defaultEmail);
                  if (!email || !email.trim()) {
                    toast.error('Please enter an email address');
                    return;
                  }
                  try {
                    toast.loading('Sending invoice...');
                    await axios.post(`${API}/b/${businessSlug}/wholesale/invoices/${selectedInvoice.id}/email?recipient_email=${encodeURIComponent(email.trim())}`);
                    toast.dismiss();
                    toast.success(`Invoice sent to ${email.trim()}`);
                    setSelectedInvoice({ ...selectedInvoice, email_sent: true, email_sent_to: email.trim() });
                  } catch (err) {
                    toast.dismiss();
                    toast.error(err.response?.data?.detail || 'Failed to send email');
                  }
                }}
                className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/20"
              >
                <Mail className="w-4 h-4 mr-1" /> Email
              </Button>
              <Link to={`/${businessSlug}/wholesale/invoices/${selectedInvoice?.id}/print`} target="_blank">
                <Button variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700" data-testid="invoice-modal-view-document-btn">
                  <FileText className="w-4 h-4 mr-1" /> View Document
                </Button>
              </Link>
              <Link to={`/${businessSlug}/wholesale/invoices/${selectedInvoice?.id}/print`}>
                <Button className="bg-gradient-to-r from-cyan-500 to-blue-600">
                  <Printer className="w-4 h-4 mr-1" /> Print
                </Button>
              </Link>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Product Confirmation Dialog */}
      <Dialog open={!!showDeleteProductConfirm} onOpenChange={(open) => !open && setShowDeleteProductConfirm(null)}>
        <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <Trash2 className="w-5 h-5" /> Delete Product
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-300">
              Are you sure you want to delete <span className="font-semibold text-white">{showDeleteProductConfirm?.name}</span>?
            </p>
            <p className="text-sm text-slate-500 mt-2">
              SKU: {showDeleteProductConfirm?.sku} • This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteProductConfirm(null)} className="border-slate-600 text-slate-300">
              Cancel
            </Button>
            <Button 
              onClick={() => handleDeleteProduct(showDeleteProductConfirm?.id)} 
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Delete Product
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Shipment Confirmation Dialog */}
      <Dialog open={!!showDeleteShipmentConfirm} onOpenChange={(open) => !open && setShowDeleteShipmentConfirm(null)}>
        <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <Trash2 className="w-5 h-5" /> Delete Shipment
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-300">
              Are you sure you want to delete shipment <span className="font-semibold text-white">{showDeleteShipmentConfirm?.container_number || showDeleteShipmentConfirm?.bill_of_lading}</span>?
            </p>
            <p className="text-sm text-slate-500 mt-2">
              BOL: {showDeleteShipmentConfirm?.bill_of_lading || 'N/A'} • This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteShipmentConfirm(null)} className="border-slate-600 text-slate-300">
              Cancel
            </Button>
            <Button 
              onClick={() => handleDeleteShipment(showDeleteShipmentConfirm?.id)} 
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Delete Shipment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Products Confirmation Dialog */}
      <Dialog open={showBulkDeleteConfirm} onOpenChange={setShowBulkDeleteConfirm}>
        <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <Trash2 className="w-5 h-5" /> Delete Multiple Products
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-300">
              Are you sure you want to delete <span className="font-semibold text-red-400">{selectedProductIds.length}</span> product{selectedProductIds.length > 1 ? 's' : ''}?
            </p>
            <p className="text-sm text-slate-500 mt-2">
              This action cannot be undone.
            </p>
            <div className="mt-4 p-3 bg-slate-700/50 rounded-lg border border-slate-600">
              <p className="text-xs text-slate-400 mb-2">Quick actions:</p>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleDeleteAllProducts}
                className="border-red-500/50 text-red-400 hover:bg-red-500/20 w-full"
                data-testid="delete-all-products-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete ALL Products ({products.length})
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkDeleteConfirm(false)} className="border-slate-600 text-slate-300">
              Cancel
            </Button>
            <Button 
              onClick={handleBulkDeleteProducts} 
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-bulk-delete-btn"
            >
              Delete {selectedProductIds.length} Product{selectedProductIds.length > 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Preview Modal */}
      <Dialog open={!!previewImage} onOpenChange={(open) => !open && setPreviewImage(null)}>
        <DialogContent className="max-w-3xl bg-slate-900 border-slate-700 p-2">
          <DialogHeader className="p-4 pb-2">
            <DialogTitle className="text-white flex items-center gap-2">
              <Package className="w-5 h-5 text-cyan-400" />
              {previewImage?.name}
            </DialogTitle>
            <p className="text-sm text-slate-400">SKU: {previewImage?.sku}</p>
          </DialogHeader>
          <div className="flex items-center justify-center p-4 bg-slate-800 rounded-lg mx-2 mb-2">
            {previewImage?.url && (
              <img 
                src={previewImage.url} 
                alt={previewImage?.name} 
                className="max-w-full max-h-[60vh] object-contain rounded-lg"
                data-testid="preview-image-large"
              />
            )}
          </div>
          <DialogFooter className="px-4 pb-4">
            <Button 
              onClick={() => setPreviewImage(null)} 
              className="bg-slate-700 hover:bg-slate-600 text-white"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Modal */}
      <Dialog open={showCustomerModal} onOpenChange={(open) => { if (!open) { setShowCustomerModal(false); setEditingCustomer(null); resetCustomerForm(); }}}>
        <DialogContent className={`max-w-lg ${isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-900'}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-cyan-400" /> {editingCustomer ? 'Edit Customer' : 'Add Customer'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div>
              <Label>Name *</Label>
              <Input value={customerForm.name} onChange={(e) => setCustomerForm({...customerForm, name: e.target.value})} className={`mt-1 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-300'}`} placeholder="Customer name" />
            </div>
            <div>
              <Label>Company</Label>
              <Input value={customerForm.company} onChange={(e) => setCustomerForm({...customerForm, company: e.target.value})} className={`mt-1 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-300'}`} placeholder="Company name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input type="email" value={customerForm.email} onChange={(e) => setCustomerForm({...customerForm, email: e.target.value})} className={`mt-1 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-300'}`} placeholder="email@example.com" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={customerForm.phone} onChange={(e) => setCustomerForm({...customerForm, phone: e.target.value})} className={`mt-1 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-300'}`} placeholder="(555) 123-4567" />
              </div>
            </div>
            <div>
              <Label>Address</Label>
              <Input value={customerForm.address} onChange={(e) => setCustomerForm({...customerForm, address: e.target.value})} className={`mt-1 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-300'}`} placeholder="Street address" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>City</Label>
                <Input value={customerForm.city} onChange={(e) => setCustomerForm({...customerForm, city: e.target.value})} className={`mt-1 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-300'}`} placeholder="City" />
              </div>
              <div>
                <Label>State</Label>
                <Input value={customerForm.state} onChange={(e) => setCustomerForm({...customerForm, state: e.target.value})} className={`mt-1 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-300'}`} placeholder="State" />
              </div>
              <div>
                <Label>ZIP</Label>
                <Input value={customerForm.zip_code} onChange={(e) => setCustomerForm({...customerForm, zip_code: e.target.value})} className={`mt-1 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-300'}`} placeholder="ZIP" />
              </div>
            </div>
            <div>
              <Label>Credit Terms</Label>
              <Select value={customerForm.credit_terms} onValueChange={(v) => setCustomerForm({...customerForm, credit_terms: v})}>
                <SelectTrigger className={`mt-1 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-300'}`}>
                  <SelectValue placeholder="Select terms" />
                </SelectTrigger>
                <SelectContent className={isDark ? 'bg-slate-700 border-slate-600' : ''}>
                  <SelectItem value="cod">COD</SelectItem>
                  <SelectItem value="net_15">Net 15</SelectItem>
                  <SelectItem value="net_30">Net 30</SelectItem>
                  <SelectItem value="net_60">Net 60</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={customerForm.notes} onChange={(e) => setCustomerForm({...customerForm, notes: e.target.value})} className={`mt-1 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-300'}`} placeholder="Any notes about this customer..." rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCustomerModal(false); setEditingCustomer(null); resetCustomerForm(); }} className={isDark ? 'border-slate-600 text-slate-300' : ''}>
              Cancel
            </Button>
            <Button onClick={editingCustomer ? handleUpdateCustomer : handleCreateCustomer} className="bg-gradient-to-r from-cyan-500 to-blue-600">
              {editingCustomer ? 'Update' : 'Save'} Customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Customer Confirm */}
      <Dialog open={!!showDeleteCustomerConfirm} onOpenChange={() => setShowDeleteCustomerConfirm(null)}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-900'}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500">
              <Trash2 className="w-5 h-5" /> Delete Customer
            </DialogTitle>
          </DialogHeader>
          <p className={isDark ? 'text-slate-400' : 'text-gray-600'}>
            Are you sure you want to delete <span className="font-semibold text-red-400">{showDeleteCustomerConfirm?.name}</span>? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteCustomerConfirm(null)} className={isDark ? 'border-slate-600 text-slate-300' : ''}>Cancel</Button>
            <Button onClick={() => handleDeleteCustomer(showDeleteCustomerConfirm.id)} className="bg-red-500 hover:bg-red-600 text-white">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={(open) => { if (!open) { setShowPaymentModal(false); setEditingPayment(null); resetPaymentForm(); }}}>
        <DialogContent className={`max-w-lg ${isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-900'}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-cyan-400" /> {editingPayment ? 'Edit Payment' : 'Record Payment'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date *</Label>
                <Input type="date" value={paymentForm.date} onChange={(e) => setPaymentForm({...paymentForm, date: e.target.value})} className={`mt-1 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-300'}`} />
              </div>
              <div>
                <Label>Amount *</Label>
                <Input type="number" step="0.01" value={paymentForm.amount} onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})} className={`mt-1 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-300'}`} placeholder="0.00" />
              </div>
            </div>
            <div>
              <Label>Category *</Label>
              <Select value={paymentForm.category} onValueChange={(v) => setPaymentForm({...paymentForm, category: v})}>
                <SelectTrigger className={`mt-1 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-300'}`}>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className={isDark ? 'bg-slate-700 border-slate-600' : ''}>
                  {PAYMENT_CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Input value={paymentForm.description} onChange={(e) => setPaymentForm({...paymentForm, description: e.target.value})} className={`mt-1 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-300'}`} placeholder="What is this payment for?" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Reference #</Label>
                <Input value={paymentForm.reference} onChange={(e) => setPaymentForm({...paymentForm, reference: e.target.value})} className={`mt-1 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-300'}`} placeholder="Invoice/PO #" />
              </div>
              <div>
                <Label>Payment Method</Label>
                <Select value={paymentForm.payment_method} onValueChange={(v) => setPaymentForm({...paymentForm, payment_method: v})}>
                  <SelectTrigger className={`mt-1 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-300'}`}>
                    <SelectValue placeholder="Select method" />
                  </SelectTrigger>
                  <SelectContent className={isDark ? 'bg-slate-700 border-slate-600' : ''}>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="check">Check</SelectItem>
                    <SelectItem value="wire">Wire Transfer</SelectItem>
                    <SelectItem value="card">Credit Card</SelectItem>
                    <SelectItem value="zelle">Zelle</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Recipient / Paid To</Label>
              <Input value={paymentForm.recipient} onChange={(e) => setPaymentForm({...paymentForm, recipient: e.target.value})} className={`mt-1 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-300'}`} placeholder="Who received this payment?" />
            </div>
            {shipments.length > 0 && (
              <div>
                <Label>Link to Shipment</Label>
                <Select value={paymentForm.linked_shipment_id || "none"} onValueChange={(v) => setPaymentForm({...paymentForm, linked_shipment_id: v === "none" ? "" : v})}>
                  <SelectTrigger className={`mt-1 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-300'}`}>
                    <SelectValue placeholder="Optional - link to shipment" />
                  </SelectTrigger>
                  <SelectContent className={isDark ? 'bg-slate-700 border-slate-600' : ''}>
                    <SelectItem value="none">None</SelectItem>
                    {shipments.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.container_number || s.bill_of_lading || 'Shipment'}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Notes</Label>
              <Textarea value={paymentForm.notes} onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})} className={`mt-1 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-300'}`} placeholder="Additional notes..." rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowPaymentModal(false); setEditingPayment(null); resetPaymentForm(); }} className={isDark ? 'border-slate-600 text-slate-300' : ''}>
              Cancel
            </Button>
            <Button 
              onClick={editingPayment ? handleUpdatePayment : handleCreatePayment} 
              className="bg-gradient-to-r from-cyan-500 to-blue-600"
              disabled={isSubmittingPayment}
            >
              {isSubmittingPayment ? 'Processing...' : (editingPayment ? 'Update' : 'Record')} Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Payment Confirm */}
      <Dialog open={!!showDeletePaymentConfirm} onOpenChange={() => setShowDeletePaymentConfirm(null)}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-900'}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500">
              <Trash2 className="w-5 h-5" /> Delete Payment
            </DialogTitle>
          </DialogHeader>
          <p className={isDark ? 'text-slate-400' : 'text-gray-600'}>
            Are you sure you want to delete this payment of <span className="font-semibold text-red-400">{formatCurrency(showDeletePaymentConfirm?.amount)}</span>? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeletePaymentConfirm(null)} className={isDark ? 'border-slate-600 text-slate-300' : ''}>Cancel</Button>
            <Button onClick={() => handleDeletePayment(showDeletePaymentConfirm.id)} className="bg-red-500 hover:bg-red-600 text-white">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
